import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import {
  CheckCircle,
  XCircle,
  Loader2,
  PencilRuler,
  Ellipsis,
  ChevronUp,
  Filter,
  BrainCircuit,
  Bell,
  ListTodo,
  Ticket as TicketIcon,
} from 'lucide-react';
import {
  FiCheck,
  FiMail,
  FiMessageSquare,
  FiVideo,
  FiPlus,
  FiChevronDown,
  FiX,
  FiArrowRight,
  FiArrowLeft,
  FiClock,
  FiEye,
  FiRefreshCw,
  FiSearch,
  FiFlag,
  FiArchive,
  FiTrash2,
  FiEdit,
} from 'react-icons/fi';
import { VscReport } from 'react-icons/vsc';
import { LuReply, LuForward } from 'react-icons/lu';
import { FiPaperclip } from 'react-icons/fi';

import {
  getTodos,
  addTodo,
  updateTodo,
  deleteTodo,
  getWeather,
  getAllMsTodo,
  addMsTodoTask,
  updateMsTodoTask,
  createMsTodoList,
  deleteMsTodoTask,
} from '../../services/api';
import { DataState } from '../../context/DataProvider';
import TaskPlan from './TaskPlan';
import EmailIframe from './EmailIframe';
import { useTaskPlan } from '../../context/TaskPlanProvider';
import { RECENT_GOAL_COUNT } from '../../utils/constants';
import { appImages } from '../../utils/constants';
import { useNotifications } from '../../context/NotificationProvider';
import {
  QuestionItem,
  QuestionThread,
  useAgentQuestions,
} from '../agent-questions/AgentQuestions';
import { useChangeRequests } from '../../context/ChangeRequestProvider';
import { useEmails } from '../../context/EmailsProvider';
import NoMailScreen from '../dialog/NoMailScreen';
import MailFilters from './MailFilters';
import { listTime } from '../../utils/helper';
import ComposeEmail from '../dialog/ComposeEmail';
import useOutlookMailbox, { STORED_FOLDERS } from './useOutlookMailbox';
import {
  getGmailEmailDetailsFromDB,
  getOutlookEmailDetailsFromDB,
} from '../../services/api';
import BriefingReplyEmail from './BriefingReplyEmail';
import BriefingTimelinePanel from './BriefingTimelinePanel';
// import BriefingActionsPanel from './BriefingActionsPanel'; // hidden for now
import BriefingForwardEmail from './BriefingForwardEmail';
import { AttachmentItem } from '../dialog/EmailContent';
import VideoBackground, { getWeatherVideoSrc } from './VideoBackground';
import { ENABLE_RIGHT_SIDEBAR_COMMUNICATION } from '../../config/features';
import {
  ENABLE_AGENT_QUESTIONS,
  ENABLE_MEETINGS,
  ENABLE_TEAMS_DM,
} from '../../config/features';
import TeamsDmPanel from '../teams-dm/TeamsDmPanel';
import {
  listTickets,
  addTicketComment,
  retryCreateTicket,
} from '../../services/ticketsApi';
import MeetingDetailModal from '../meeting/MeetingDetailModal';
import {
  fetchGoogleCalendarEvents,
  fetchOutlookCalendarEvents,
} from '../../services/calendarApi';

const SUGGESTIONS = [];

// ─────────────────────────────────────────────────────────────────────────────
// Workspace tab helpers (from old code)
// ─────────────────────────────────────────────────────────────────────────────

function formatTimeAgo(dateInput) {
  if (!dateInput) return '';
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  if (!(date instanceof Date) || isNaN(date.getTime())) return '';
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function generateMiscText(notification) {
  if (notification.source_type === 'change_request') {
    const cr = notification.cr_data;
    return `${cr?.classification || 'Scope change'} · ${cr?.phase || ''}`;
  }
  if (notification.source_type === 'listener') {
    const listenerData = notification.listener_data || {};
    const payload = listenerData.payload || {};
    switch (listenerData.app) {
      case 'jira':
        return `Ticket #${payload.ticketId || payload.issue_key || '-'}`;
      case 'gmail':
      case 'microsoft_outlook':
        return `From: ${payload.from || 'Unknown sender'}`;
      case 'zoho_crm':
        return `Lead: ${payload.leadName || payload.record_name || 'CRM record'}, Value: $${Number(payload.dealSize || 0).toLocaleString()}`;
      case 'slack':
        return `From: ${payload.user || payload.sender_name || 'Unknown sender'} in #${payload.channel || payload.channel_name || 'channel'}`;
      default:
        return `Source: ${listenerData.app || 'external integration'}`;
    }
  }
  if (notification.source_type === 'workflow')
    return `Workflow: "${notification.workflow_data.workflow_title}"`;
  if (notification.source_type === 'meeting')
    return `Subject: ${notification.subject}`;
  return '';
}

const NotificationItem = ({ notification, onClick }) => {
  const isCR = notification.source_type === 'change_request';
  const app =
    notification.source_type === 'listener'
      ? notification.listener_data.app
      : notification.source_type === 'meeting'
        ? notification.app
        : 'agent';
  const miscText = generateMiscText(notification);
  return (
    <button
      onClick={onClick}
      className="flex w-full cursor-pointer items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-zinc-800/60"
    >
      {isCR ? (
        <div className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded bg-amber-500/20 text-[10px] font-bold text-amber-400">
          CR
        </div>
      ) : (
        <img
          src={appImages[app]}
          alt={app}
          className="mt-0.5 h-5 w-5 flex-shrink-0 rounded"
        />
      )}
      <div className="flex-1 overflow-hidden">
        <div className="flex items-center justify-between">
          <p className="truncate text-xs font-medium text-zinc-200">
            {notification.event_name}
          </p>
          <p className="ml-2 flex-shrink-0 text-[10px] text-zinc-500">
            {formatTimeAgo(notification.timestamp)}
          </p>
        </div>
        <p className="mt-0.5 truncate text-[11px] text-zinc-400">{miscText}</p>
      </div>
    </button>
  );
};

const WaitingEventItem = ({ event, onClick }) => {
  const tasks = event.llm_summary?.task_wise_summary || [];
  const jiraUpdateCount = tasks.reduce(
    (s, t) => s + (t.proposed_jira_issue_updates?.length || 0),
    0,
  );
  const actionCount = (event.llm_summary?.extracted_action_items || []).length;
  const blockerCount = (event.llm_summary?.unique_blockers || []).length;

  const chips = [
    jiraUpdateCount > 0 &&
      `${jiraUpdateCount} Jira update${jiraUpdateCount > 1 ? 's' : ''}`,
    actionCount > 0 && `${actionCount} action${actionCount > 1 ? 's' : ''}`,
    blockerCount > 0 && `${blockerCount} blocker${blockerCount > 1 ? 's' : ''}`,
  ].filter(Boolean);

  return (
    // eslint-disable-next-line
    <div
      onClick={onClick}
      className="flex cursor-pointer items-start gap-3 px-4 py-3 transition-colors hover:bg-zinc-800/60"
    >
      <img
        src={appImages[event.app] || appImages.microsoft_teams}
        alt={event.app}
        className="mt-0.5 h-5 w-5 flex-shrink-0 rounded"
      />
      <div className="flex-1 overflow-hidden">
        <div className="flex items-center justify-between">
          <p className="text-xs font-medium text-zinc-200">
            {event.event_name || event.subject}
          </p>
          <p className="ml-2 flex-shrink-0 text-[10px] text-zinc-500">
            {formatTimeAgo(event.timestamp)}
          </p>
        </div>
        {chips.length > 0 ? (
          <div className="mt-1 flex flex-wrap gap-1">
            {chips.map((chip) => (
              <span
                key={chip}
                className="rounded-full bg-amber-500/10 px-1.5 py-0.5 text-[9px] font-semibold text-amber-400"
              >
                {chip}
              </span>
            ))}
          </div>
        ) : (
          <p className="mt-0.5 truncate text-[11px] text-zinc-400">
            Pending review
          </p>
        )}
      </div>
    </div>
  );
};

const TicketItem = ({ ticket, onClick }) => {
  const isWaiting = ticket.status === 'CLARIFICATION_REQUIRED';
  return (
    // eslint-disable-next-line
    <div
      onClick={onClick}
      className="flex cursor-pointer items-start gap-3 px-4 py-3 transition-colors hover:bg-zinc-800/60"
    >
      <TicketIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-zinc-500" />
      <div className="flex-1 overflow-hidden">
        <div className="flex items-center justify-between">
          <p className="truncate text-xs font-medium text-zinc-200">
            {ticket.title}
          </p>
          <p className="ml-2 flex-shrink-0 text-[10px] text-zinc-500">
            {formatTimeAgo(ticket.created_at)}
          </p>
        </div>
        <div className="mt-1">
          <span
            className={`rounded-full px-1.5 py-0.5 text-[9px] font-semibold ${
              isWaiting
                ? 'bg-amber-500/10 text-amber-400'
                : 'bg-emerald-500/10 text-emerald-400'
            }`}
          >
            {isWaiting
              ? 'Clarification needed'
              : `Created${ticket.jira_issue_key ? ` · ${ticket.jira_issue_key}` : ''}`}
          </span>
        </div>
      </div>
    </div>
  );
};

// Hands a sidebar item to the agent through the generic listener context
// (sent as <event app=...>{json}</event>, like EmailPanel's "Chat with Agent").
// _noReopen: the chat chip has no modal to reopen for these items.
const stageItemForAgent = (onStageChat, app, name, id, data) =>
  onStageChat?.(
    {
      _synthetic: true,
      _noReopen: true,
      source_type: 'listener',
      event_name: name || app,
      id: `${app}:${id}`,
      listener_data: { app, payload: data },
    },
    `${app}:${id}`,
  );

const ChatWithAgentButton = ({ onClick, compact = false }) => (
  <button
    onClick={onClick}
    title="Chat with Agent"
    aria-label="Chat with Agent"
    className={
      compact
        ? 'mt-0.5 text-zinc-600 opacity-0 transition-opacity hover:text-[#9b98f5] focus:opacity-100 group-hover:opacity-100'
        : 'flex items-center gap-1.5 rounded-lg bg-[#5652CF] px-3 py-1.5 text-[11px] font-medium text-white transition-colors hover:bg-[#5652CF]/80'
    }
  >
    <FiMessageSquare className="h-3.5 w-3.5" />
    {!compact && <span>Chat with Agent</span>}
  </button>
);

const TicketDetailModal = ({
  ticket,
  accessToken,
  onClose,
  onUpdated,
  onStageChat,
}) => {
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submitComment = async () => {
    if (!comment.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const res = await addTicketComment(
        accessToken,
        ticket.ticket_id,
        comment.trim(),
      );
      setComment('');
      onUpdated(res.ticket);
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setBusy(false);
    }
  };

  const handleCreate = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await retryCreateTicket(accessToken, ticket.ticket_id);
      onUpdated(res.ticket);
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setBusy(false);
    }
  };

  const isWaiting = ticket.status === 'CLARIFICATION_REQUIRED';

  return (
    // eslint-disable-next-line
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={onClose}
    >
      {/* eslint-disable-next-line */}
      <div
        className="max-h-[80vh] w-full max-w-md overflow-y-auto rounded-xl border border-zinc-800 bg-zinc-900 p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-start justify-between gap-2">
          <h3 className="text-sm font-semibold text-zinc-100">
            {ticket.title}
          </h3>
          <div className="flex shrink-0 items-center gap-2">
            {onStageChat && (
              <ChatWithAgentButton
                onClick={() => {
                  stageItemForAgent(
                    onStageChat,
                    'ticket',
                    ticket.title,
                    ticket.ticket_id,
                    ticket,
                  );
                  onClose();
                }}
              />
            )}
            <button
              onClick={onClose}
              className="text-zinc-500 hover:text-zinc-300"
            >
              <FiX size={18} />
            </button>
          </div>
        </div>
        <p className="mb-3 whitespace-pre-wrap text-xs text-zinc-400">
          {ticket.description}
        </p>

        {isWaiting ? (
          <div className="mb-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-300">
            {ticket.classification?.reason ||
              'Needs more detail before this can become a real ticket.'}
          </div>
        ) : (
          <div className="mb-3 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-300">
            Created{ticket.jira_issue_key ? ` — ${ticket.jira_issue_key}` : ''}
            {ticket.jira_browse_url && (
              <a
                href={ticket.jira_browse_url}
                target="_blank"
                rel="noreferrer"
                className="ml-2 underline"
              >
                Open in Jira
              </a>
            )}
          </div>
        )}

        {ticket.comments?.length > 0 && (
          <div className="mb-3 space-y-2">
            {ticket.comments.map((c, i) => (
              <div
                key={i}
                className="rounded-lg bg-zinc-800/60 px-3 py-2 text-xs text-zinc-300"
              >
                {c.text}
              </div>
            ))}
          </div>
        )}

        {error && (
          <div className="mb-3 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-400">
            {error}
          </div>
        )}

        {isWaiting && (
          <>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              placeholder="Add a clarifying comment..."
              className="mb-2 w-full rounded-lg border border-zinc-700 bg-zinc-800 px-3 py-2 text-xs text-zinc-200 outline-none"
            />
            <div className="flex gap-2">
              <button
                onClick={submitComment}
                disabled={busy || !comment.trim()}
                className="rounded-lg bg-zinc-700 px-3 py-1.5 text-xs font-medium text-zinc-200 hover:bg-zinc-600 disabled:opacity-40"
              >
                Add comment
              </button>
              <button
                onClick={handleCreate}
                disabled={busy}
                className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-500 disabled:opacity-40"
              >
                Create ticket
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const WaitingForTask = () => (
  <div className="flex flex-1 flex-col items-center justify-center p-6 text-center text-zinc-500">
    <PencilRuler className="mb-4 h-10 w-10 text-zinc-600" strokeWidth={1.5} />
    <h4 className="mb-1 text-sm font-semibold text-zinc-400">
      Workspace Ready
    </h4>
    <p className="text-xs">
      Enter a task to see the agent's plan and actions here.
    </p>
  </div>
);

const ActionItem = ({ action, onActionClick }) => {
  let description = `Call ${action.function?.name}`;
  try {
    const args = JSON.parse(action.function?.arguments || '{}');
    description = args.action_description || description;
  } catch (e) {}

  const isRunning = action.status === 'running';
  const getStatusDetails = () => {
    if (isRunning) {
      switch (action.running_phase || 'calling_tool') {
        case 'filtering':
          return {
            icon: <Filter className="h-4 w-4 animate-pulse text-cyan-400" />,
            textColor: 'text-cyan-300',
          };
        case 'analyzing':
          return {
            icon: <BrainCircuit className="h-4 w-4 text-indigo-400" />,
            textColor: 'text-indigo-300',
          };
        default:
          return {
            icon: <Loader2 className="h-4 w-4 animate-spin text-blue-400" />,
            textColor: 'text-blue-300',
          };
      }
    }
    if (action.status === 'success')
      return {
        icon: <CheckCircle className="h-4 w-4 text-emerald-400" />,
        textColor: 'text-zinc-300',
      };
    if (action.status === 'failed')
      return {
        icon: <XCircle className="h-4 w-4 text-red-400" />,
        textColor: 'text-red-300',
      };
    return { icon: null, textColor: 'text-zinc-400' };
  };

  const { icon, textColor } = getStatusDetails();
  return (
    <button
      onClick={() => onActionClick(action.id)}
      className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-zinc-800/60"
    >
      <div className="w-4 flex-shrink-0">{icon}</div>
      <p className={`flex-1 truncate text-xs font-medium ${textColor}`}>
        {description}
      </p>
    </button>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Personal tab shared atoms
// ─────────────────────────────────────────────────────────────────────────────

function Av({ initials, color }) {
  return (
    <span
      className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold text-white ${color}`}
    >
      {initials}
    </span>
  );
}

function Label({ children }) {
  return (
    <p className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">
      {children}
    </p>
  );
}

// ─── Prep Modal ───────────────────────────────────────────────────────────────

function PrepModal({ meeting, onClose }) {
  if (!meeting?.prep) return null;
  const { who, about, fit, angles, team, flow } = meeting.prep;
  const sections = [
    [
      "Who you're meeting",
      <p key="w">
        <span className="font-semibold text-zinc-100">Anil Pradhan</span>, {who}
      </p>,
    ],
    ['What Technosport actually is', <p key="a">{about}</p>],
    ['Why SuperManager AGI fits', <p key="f">{fit}</p>],
    [
      'Angles that should land',
      <ul key="ang" className="space-y-1.5">
        {angles.map((a, i) => (
          <li key={i} className="flex items-start gap-2.5">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-500" />
            {a}
          </li>
        ))}
      </ul>,
    ],
    ['Your team on the call', <p key="t">{team}</p>],
    ['Standard demo flow', <p key="fl">{flow}</p>],
  ];
  return (
    <div className="absolute inset-0 z-50 flex items-center justify-center rounded-xl bg-black/70 p-4 backdrop-blur-sm">
      <div className="relative flex max-h-[90%] w-full max-w-xl flex-col overflow-hidden rounded-2xl border border-zinc-700 bg-zinc-900 shadow-2xl">
        <div className="flex shrink-0 items-center justify-between border-b border-zinc-800 px-5 py-3.5">
          <p className="text-sm font-semibold text-zinc-100">Meeting Prep</p>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-zinc-100"
          >
            <FiX className="h-4 w-4" />
          </button>
        </div>
        <div className="sc space-y-4 overflow-y-auto px-5 py-4 text-sm leading-relaxed text-zinc-300">
          {sections.map(([label, content]) => (
            <section key={label}>
              <Label>{label}</Label>
              <div className="mt-1.5">{content}</div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Email Panel (real data from useEmails) ───────────────────────────────────

// Priority badge helper — matches Dialog.jsx logic
function PriorityBadge({ priority }) {
  const isLow = priority <= 1;
  const isMed = priority <= 3;
  const label = isLow ? 'Low' : isMed ? 'Medium' : 'High';
  const color = isLow ? 'bg-[#5652CF]' : isMed ? 'bg-yellow-500' : 'bg-red-500';
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[9px] font-semibold text-white ${color}`}
    >
      {label}
    </span>
  );
}

// Shimmer placeholder shown while a per-email detail fetch is in flight
function SummarySkeleton() {
  return (
    <div className="space-y-2 px-3 pb-3 pt-3">
      <div className="h-2 w-16 animate-pulse rounded-full bg-zinc-700/60" />
      <div className="space-y-1.5">
        <div className="h-3 w-full animate-pulse rounded-full bg-zinc-700/60" />
        <div className="h-3 w-[90%] animate-pulse rounded-full bg-zinc-700/60 [animation-delay:75ms]" />
        <div className="h-3 w-[70%] animate-pulse rounded-full bg-zinc-700/60 [animation-delay:150ms]" />
      </div>
    </div>
  );
}

// Gmail gives a string, Outlook {name, contact}; a mail with no display name
// must not hand React the object.
const senderName = (from) =>
  typeof from === 'string' ? from : from?.name || from?.contact || '';

// Inline expanded detail — summary + CTA
function EmailDetail({
  email,
  isLoading,
  onClose,
  onChatWithAgent,
  onViewEmailInBriefing,
  onAction,
  folders = [],
}) {
  const [showAllPoints, setShowAllPoints] = useState(false);
  const subject = email?.subject || '(no subject)';
  const from = senderName(email?.from) || 'Unknown';
  const body = email?.snippet || email?.bodyPreview || '';

  // Real fields from the email analysis pipeline————————————————
  const aiSummary = email?.summary || null;
  const keyPoints = [
    ...(email?.criticalPoints || []),
    ...(email?.insights || []),
  ];
  const visiblePoints = showAllPoints ? keyPoints : keyPoints.slice(0, 2);

  // Mail the pipeline never analysed (Other, live folders) shows its preview,
  // not text dressed up as an AI summary.
  const displaySummary =
    aiSummary ||
    `${body.slice(0, 160)}${body.length > 160 ? '…' : ''}` ||
    `Email from ${from} — "${subject}".`;

  return (
    <div className="animate-fi mx-3 mb-3 overflow-hidden rounded-xl border border-zinc-700 bg-zinc-800/50">
      {/* ── AI Summary ─────────────────────────────────────────── */}
      {isLoading ? (
        <SummarySkeleton />
      ) : (
        <div className="space-y-2 px-3 pb-3 pt-3">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">
            {aiSummary ? 'AI Summary' : 'Preview'}
          </p>
          <p className="text-xs leading-relaxed text-zinc-300">
            {displaySummary}
          </p>

          {keyPoints.length > 0 && (
            <>
              <div className="my-1 h-px w-full bg-zinc-700/50" />
              <p className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">
                Key Points
              </p>
              <ul className="space-y-1 pl-0.5">
                {visiblePoints.map((pt, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2 text-xs leading-snug text-zinc-300"
                  >
                    <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-zinc-500" />
                    {pt}
                  </li>
                ))}
              </ul>
              {keyPoints.length > 2 && (
                <button
                  onClick={() => setShowAllPoints((p) => !p)}
                  className="text-[10px] font-medium text-[#9b98f5] hover:underline"
                >
                  {showAllPoints
                    ? 'Show less'
                    : `+${keyPoints.length - 2} more`}
                </button>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Outlook actions: Graph first, then the list follows ── */}
      {onAction && (
        <div className="flex items-center gap-1 px-2.5 pt-1">
          {[
            [
              email?.isRead ? 'Mark unread' : 'Mark read',
              FiMail,
              () => onAction(email, 'read', !email?.isRead),
            ],
            [
              email?.flagStatus === 'flagged' ? 'Unflag' : 'Flag',
              FiFlag,
              () => onAction(email, 'flag', email?.flagStatus !== 'flagged'),
            ],
            ['Archive', FiArchive, () => onAction(email, 'move', 'archive')],
            ['Delete', FiTrash2, () => onAction(email, 'delete')],
          ].map(([label, Icon, run]) => (
            <button
              key={label}
              onClick={run}
              title={label}
              aria-label={label}
              className={`rounded-lg p-1.5 transition-colors hover:bg-zinc-700 hover:text-zinc-200 ${
                label === 'Unflag' ? 'text-amber-400' : 'text-zinc-500'
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
            </button>
          ))}
          {folders.length > 0 && (
            <select
              value=""
              aria-label="Move to folder"
              onChange={(e) =>
                e.target.value && onAction(email, 'move', e.target.value)
              }
              className="ml-auto max-w-[8rem] rounded-lg border border-zinc-700 bg-zinc-800 px-1.5 py-1 text-[10px] text-zinc-300 outline-none"
            >
              <option value="">Move to…</option>
              {folders
                .filter((f) => f.id !== email?.parentFolderId)
                .map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
            </select>
          )}
        </div>
      )}

      {/* ── actions ────────────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-1.5 px-2.5 pb-3 pt-1">
        <button
          onClick={() => onChatWithAgent(email)}
          className="flex items-center justify-center gap-1.5 rounded-lg bg-[#5652CF] py-2 text-[11px] font-medium text-white transition-colors hover:bg-[#5652CF]/80"
          style={{ flex: '2 1 0%', minWidth: 0 }}
        >
          <FiMail className="h-3.5 w-3.5 flex-shrink-0" />
          <span className="truncate">Chat with Agent</span>
        </button>
        {onViewEmailInBriefing && (
          <button
            onClick={() => onViewEmailInBriefing(email)}
            className="flex items-center justify-center gap-1.5 rounded-lg bg-zinc-700 py-2 text-[11px] font-medium text-white transition-colors hover:bg-zinc-600"
            style={{ flex: '1 1 0%', minWidth: 0 }}
          >
            <FiEye className="h-3.5 w-3.5 flex-shrink-0" />
            <span className="truncate">View</span>
          </button>
        )}
        <button
          onClick={onClose}
          className="rounded-lg p-1.5 text-zinc-500 transition-colors hover:bg-zinc-700 hover:text-zinc-200"
        >
          <FiX className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

function EmailPanel({
  onStageChat,
  onViewEmailInBriefing,
  expandedEmailId,
  setExpandedEmailId,
  onExpandEmail,
}) {
  // ── real data ──
  const {
    emails,
    currentPage,
    setCurrentPage,
    emailfilter,
    setEmailFilter,
    loadingEmails,
    totalPages,
    isOutlook,
    isGmailAuthenticated,
    isOutlookAuthenticated,
    isGmailSynchronized,
    isOutlookSynchronized,
    emailStats,
    setEmails,
    refreshKey,
    setRefreshKey,
    setForceRefetch,
  } = useEmails();

  const { userGoogleId } = DataState();

  const mailbox = useOutlookMailbox({
    token: userGoogleId,
    enabled: isOutlook && isOutlookAuthenticated && isOutlookSynchronized,
    refreshKey,
  });
  const [searchText, setSearchText] = useState('');
  const [composeOpen, setComposeOpen] = useState(false);
  const [detailCache, setDetailCache] = useState({}); // message_id → full email detail
  const [loadingDetails, setLoadingDetails] = useState(new Set()); // message_ids currently fetching
  const [filterOpen, setFilterOpen] = useState(false);
  const sortKey = 'date';
  const filterRef = useRef(null);

  // Fetch full email detail (with summary, criticalPoints, insights…) on expand
  const fetchDetail = async (email) => {
    const msgId = email?.message_id || email?.id;
    if (!msgId || detailCache[msgId]) return; // already cached
    setLoadingDetails((prev) => new Set(prev).add(msgId));
    try {
      const fn = isOutlook
        ? getOutlookEmailDetailsFromDB
        : getGmailEmailDetailsFromDB;
      const detail = await fn(msgId, userGoogleId);
      if (detail) setDetailCache((prev) => ({ ...prev, [msgId]: detail }));
    } catch (e) {
      toast.error('Failed to load email details.');
    } finally {
      setLoadingDetails((prev) => {
        const next = new Set(prev);
        next.delete(msgId);
        return next;
      });
    }
  };

  useEffect(() => {
    if (expandedEmailId && onExpandEmail) {
      const email = (emails || []).find(
        (e) => (e.message_id || e.id) === expandedEmailId,
      );
      // A mail opened from a live view (Other, a folder, search) is not in
      // the stored list: fetch it by id.
      fetchDetail(email || { message_id: expandedEmailId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedEmailId, emails]);

  useEffect(() => {
    if (expandedEmailId && onExpandEmail) {
      const email = (emails || []).find(
        (e) => (e.message_id || e.id) === expandedEmailId,
      );
      const detail = detailCache[expandedEmailId] || email;
      if (detail) onExpandEmail(detail);
    } else if (!expandedEmailId && onExpandEmail) {
      onExpandEmail(null);
    }
  }, [expandedEmailId, detailCache, emails, onExpandEmail]);

  // close filter on outside click
  useEffect(() => {
    const handler = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target))
        setFilterOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleFilterChange = (key, value) => {
    setEmailFilter((prev) => {
      const next = { ...prev };
      if (key === 'Inbox' || key === 'SentItems') {
        delete next[key === 'Inbox' ? 'SentItems' : 'Inbox'];
      }
      if (next[key] === value) delete next[key];
      else next[key] = value;
      return next;
    });
  };

  const handleChatWithAgent = (email) => {
    if (!onStageChat) return;
    const appLabel = isOutlook ? 'microsoft_outlook' : 'gmail';
    const msgId = email?.message_id || email?.id;
    const payload = {
      _synthetic: true, // not a real DB notification
      source_type: 'listener',
      event_name: email?.subject || 'Email',
      id: msgId || String(Date.now()),
      message_id: msgId,
      isOutlook: isOutlook,
      emailData: email,
      listener_data: {
        app: appLabel,
        payload: {
          from:
            email?.from?.address ||
            email?.from?.contact ||
            senderName(email?.from),
          to: email?.to || '',
          subject: email?.subject || '',
          body: email?.bodyPreview || email?.snippet || '',
        },
      },
    };
    onStageChat(payload, payload.id);
  };

  const FILTER_KEYS = ['isRead', 'urgency', 'senderClass', 'objectiveClass'];
  const filterCount = FILTER_KEYS.filter(
    (key) => emailfilter[key] !== undefined,
  ).length;

  const pickFolder = (folder) => {
    if (STORED_FOLDERS[folder]) {
      const key = folder === 'inbox' ? 'Inbox' : 'SentItems';
      setEmailFilter((prev) => {
        const next = { ...prev };
        delete next.Inbox;
        delete next.SentItems;
        next[key] = key;
        return next;
      });
      setCurrentPage(1);
    }
    setSearchText('');
    mailbox.changeView({ folder, search: '' });
  };

  const handleMailAction = async (email, action, value) => {
    const msgId = email?.message_id || email?.id;
    const updated = await mailbox.act(email, action, value);
    if (updated === undefined) return;
    if (updated === null) setExpandedEmailId?.(null);
    setDetailCache((prev) => ({
      ...prev,
      [msgId]: updated ? { ...(prev[msgId] || email), ...updated } : undefined,
    }));
    if (mailbox.isLive) return;
    if (updated) {
      // Read or flag: change the row in place; the cached pages reload later.
      setEmails((prev) =>
        prev.map((e) =>
          (e.message_id || e.id) === msgId
            ? { ...e, ...updated, from: e.from }
            : e,
        ),
      );
    } else {
      setForceRefetch(true);
      setRefreshKey((k) => k + 1);
    }
  };

  const otherFolders = mailbox.folders.filter(
    (f) => !Object.values(STORED_FOLDERS).includes(f.name),
  );
  const unreadOf = (name) =>
    mailbox.folders.find((f) => f.name === name)?.unread || 0;
  const folderOptions = [
    { value: 'inbox', label: 'Inbox', unread: unreadOf('Inbox') },
    { value: 'sentitems', label: 'Sent', unread: 0 },
    ...otherFolders.map((f) => ({
      value: f.id,
      label: f.name,
      unread: f.unread,
    })),
  ];

  const nextPage = () =>
    mailbox.isLive
      ? mailbox.setLivePage(mailbox.livePage + 1)
      : setCurrentPage((p) => Math.min(p + 1, totalPages));
  const prevPage = () =>
    mailbox.isLive
      ? mailbox.setLivePage(Math.max(mailbox.livePage - 1, 1))
      : setCurrentPage((p) => Math.max(p - 1, 1));

  // client-side sort only — MailFilters drives filtering via setEmailFilter (server-side)
  const rawList = isOutlook
    ? (mailbox.isLive ? mailbox.liveEmails : emails || []).map((e) => ({
        ...e,
        snippet: e.bodyPreview,
      }))
    : emails || [];
  const listLoading = mailbox.isLive ? mailbox.liveLoading : loadingEmails;

  const sorted = [...rawList].sort((a, b) => {
    if (sortKey === 'sender') {
      const na = senderName(a.from);
      const nb = senderName(b.from);
      return na.localeCompare(nb);
    }
    return new Date(b.date) - new Date(a.date);
  });

  const isConnected = isOutlook ? isOutlookAuthenticated : isGmailAuthenticated;
  const isSynced = isOutlook ? isOutlookSynchronized : isGmailSynchronized;

  // ── not connected ──
  if (!isConnected) {
    return (
      <div className="flex h-full w-full flex-1 flex-col gap-2">
        <div className="mx-auto flex h-full w-full flex-col items-center overflow-hidden ">
          <NoMailScreen
            isOutlook={isOutlook}
            noneAuthenticated
            isGmailSynchronized={isGmailSynchronized}
            isOutlookSynchronized={isOutlookSynchronized}
          />
        </div>
      </div>
    );
  }

  // ── connected but not yet synced ──
  if (!isSynced) {
    return (
      <div className="flex h-full w-full flex-1 flex-col gap-2">
        <div className="mx-auto flex h-full w-full flex-col items-center overflow-hidden ">
          <NoMailScreen
            isOutlook={isOutlook}
            isGmailSynchronized={isGmailSynchronized}
            isOutlookSynchronized={isOutlookSynchronized}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      {/* ── toolbar ── */}
      <div className="relative z-20 flex shrink-0 items-center justify-between border-b border-zinc-800 px-3 py-2">
        {/* folder picker + Focused/Other */}
        <div className="flex min-w-0 flex-1 items-center gap-1.5">
          <select
            value={mailbox.view.folder}
            onChange={(e) => pickFolder(e.target.value)}
            aria-label="Folder"
            className="min-w-0 flex-1 truncate rounded-lg border border-zinc-700 bg-zinc-800/60 px-2 py-1 text-[10px] font-medium text-zinc-200 outline-none"
          >
            {folderOptions.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
                {f.unread ? ` (${f.unread})` : ''}
              </option>
            ))}
          </select>
          {mailbox.view.folder === 'inbox' && !mailbox.view.search && (
            <div className="flex shrink-0 items-center gap-1 rounded-lg bg-zinc-800/60 p-0.5">
              {[
                ['focused', 'Focused'],
                ['other', 'Other'],
              ].map(([key, lbl]) => (
                <button
                  key={key}
                  onClick={() => mailbox.changeView({ focused: key })}
                  className={`rounded-md px-2.5 py-1 text-[10px] font-medium transition-all
                    ${mailbox.view.focused === key ? 'bg-zinc-700 text-zinc-100' : 'text-zinc-500 hover:text-zinc-300'}`}
                >
                  {lbl}
                </button>
              ))}
            </div>
          )}
        </div>

        {isOutlook && (
          <button
            onClick={() => setComposeOpen(true)}
            aria-label="New email"
            title="New email"
            className="ml-1.5 shrink-0 rounded-lg p-1.5 text-zinc-500 transition-colors hover:bg-zinc-800 hover:text-zinc-300"
          >
            <FiEdit className="h-3.5 w-3.5" />
          </button>
        )}
        <button
          onClick={() => {
            setForceRefetch(true);
            setRefreshKey((k) => k + 1); // stored list, folders and live list
          }}
          disabled={listLoading}
          aria-label="Refresh mail"
          title="Refresh"
          className="shrink-0 rounded-lg p-1.5 text-zinc-500 transition-colors hover:bg-zinc-800 hover:text-zinc-300 disabled:opacity-50"
        >
          <FiRefreshCw
            className={`h-3.5 w-3.5 ${listLoading ? 'animate-spin' : ''}`}
          />
        </button>
        {/* filter: AI fields of the stored Inbox/Sent list, so hidden on live views */}
        {!mailbox.isLive && (
          <div className="relative shrink-0" ref={filterRef}>
            <button
              onClick={() => setFilterOpen((p) => !p)}
              aria-label="Filter mail"
              className={`relative rounded-lg p-1.5 transition-colors ${filterOpen ? 'bg-zinc-700 text-zinc-100' : 'text-zinc-500 hover:bg-zinc-800 hover:text-zinc-300'}`}
            >
              <Filter className="h-3.5 w-3.5" />
              {filterCount > 0 && (
                <span className="absolute right-0.5 top-0.5 h-1.5 w-1.5 rounded-full bg-[#5652CF]" />
              )}
            </button>

            {filterOpen && (
              <div className="absolute right-0 top-full z-50 mt-1">
                <MailFilters
                  emailStats={emailStats}
                  emailfilter={emailfilter}
                  onToggle={handleFilterChange}
                  onClear={() => {
                    setEmailFilter((prev) => {
                      const next = { ...prev };
                      FILTER_KEYS.forEach((key) => delete next[key]);
                      return next;
                    });
                    setCurrentPage(1);
                  }}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* The full compose modal from the mail page (AI draft, attachments),
          portalled so the sidebar's blur can't clip a fixed overlay. */}
      {composeOpen &&
        createPortal(
          <ComposeEmail
            setIsComposeEmailModalOpen={setComposeOpen}
            isOutlook={isOutlook}
          />,
          document.body,
        )}

      {/* ── search (whole mailbox, live from Outlook) ── */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          mailbox.changeView({ search: searchText.trim() });
        }}
        className="flex shrink-0 items-center gap-2 border-b border-zinc-800 px-3 py-1.5"
      >
        <FiSearch className="h-3 w-3 shrink-0 text-zinc-500" />
        <input
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder="Search all mail"
          aria-label="Search mail"
          className="min-w-0 flex-1 bg-transparent text-[11px] text-zinc-200 outline-none placeholder:text-zinc-600"
        />
        {mailbox.view.search && (
          <button
            type="button"
            aria-label="Clear search"
            onClick={() => {
              setSearchText('');
              mailbox.changeView({ search: '' });
            }}
            className="text-zinc-500 hover:text-zinc-300"
          >
            <FiX className="h-3 w-3" />
          </button>
        )}
      </form>

      {/* ── list ── */}
      <div className="sc flex-1 overflow-y-auto">
        {/* A background reload (socket event, mark-read) keeps the rows on
            screen, so an open mail is not swapped out mid-click. */}
        {listLoading && sorted.length === 0 ? (
          // skeleton
          <div className="divide-y divide-zinc-800/50">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="flex animate-pulse items-start gap-3 px-4 py-3"
              >
                <div className="h-7 w-7 shrink-0 rounded-full bg-zinc-800" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-3 w-3/4 rounded bg-zinc-800" />
                  <div className="h-2.5 w-full rounded bg-zinc-800/60" />
                </div>
              </div>
            ))}
          </div>
        ) : sorted.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center">
            <FiMail className="h-7 w-7 text-zinc-600" />
            <p className="text-xs text-zinc-500">No emails found</p>
          </div>
        ) : (
          <div className="divide-y divide-zinc-800/50">
            {sorted.map((email, i) => {
              const id = email.id || i;
              const isOpen = expandedEmailId === id;
              const from = senderName(email?.from) || 'Unknown';
              const subject = email?.subject || '(no subject)';
              const snippet = (
                email?.snippet ||
                email?.bodyPreview ||
                ''
              ).slice(0, 80);
              const date = listTime(email?.date);

              return (
                <div key={id}>
                  <button
                    onClick={() => {
                      const newId = id === expandedEmailId ? null : id;
                      setExpandedEmailId?.(newId);
                      if (newId !== null) fetchDetail(email);
                      // Opening a mail reads it, as in Outlook.
                      if (newId !== null && isOutlook && !email.isRead)
                        handleMailAction(email, 'read', true);
                    }}
                    className={`flex w-full items-start gap-3 px-4 py-3 text-left transition-colors
                      ${isOpen ? 'bg-zinc-800/60' : 'hover:bg-zinc-800/40'}`}
                  >
                    {/* avatar */}
                    <div
                      className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold text-white
                      ${['bg-violet-600', 'bg-blue-600', 'bg-emerald-600', 'bg-amber-600', 'bg-rose-600', 'bg-cyan-600'][i % 6]}`}
                    >
                      {from[0]?.toUpperCase()}
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <p
                          className={`truncate text-xs ${!email.isRead || emailfilter['SentItems'] ? 'font-semibold text-zinc-100' : 'text-zinc-300'}`}
                        >
                          {from.length > 20 ? from.slice(0, 20) + '…' : from}
                        </p>
                        <div className="flex shrink-0 items-center gap-1.5">
                          {email.priority !== undefined && (
                            <PriorityBadge priority={email.priority} />
                          )}
                          <span className="text-[10px] text-zinc-600">
                            {date}
                          </span>
                        </div>
                      </div>
                      <p className="mt-0.5 truncate text-[11px] text-zinc-400">
                        {subject}
                      </p>
                      {!isOpen && (
                        <p className="mt-0.5 truncate text-[10px] text-zinc-600">
                          {snippet}
                        </p>
                      )}
                      {/* Sentiment + category labels in header */}
                      {(email.sentiment ||
                        (email.objectiveClass &&
                          email.objectiveClass.length > 0)) && (
                        <div className="mt-1 flex flex-wrap items-center gap-1">
                          {email.sentiment && (
                            <span className="rounded-full border border-zinc-600 bg-zinc-700 px-1.5 py-0.5 text-[9px] font-medium text-zinc-300">
                              {email.sentiment}
                            </span>
                          )}
                          {(Array.isArray(email.objectiveClass)
                            ? email.objectiveClass
                            : email.objectiveClass
                              ? [email.objectiveClass]
                              : []
                          ).map((tag, ti) => (
                            <span
                              key={ti}
                              title={tag}
                              className="max-w-full truncate rounded-full border border-[#5652CF]/30 bg-[#5652CF]/20 px-1.5 py-0.5 text-[9px] font-medium text-[#9b98f5]"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    <FiChevronDown
                      className={`mt-1 h-3 w-3 shrink-0 text-zinc-600 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                    />
                  </button>

                  {isOpen &&
                    (() => {
                      const msgId = email?.message_id || email?.id;
                      const rich = (msgId && detailCache[msgId]) || email;
                      const isLoading = msgId
                        ? loadingDetails.has(msgId)
                        : false;
                      return (
                        <EmailDetail
                          email={rich}
                          isLoading={isLoading}
                          onClose={() => setExpandedEmailId?.(null)}
                          onChatWithAgent={handleChatWithAgent}
                          onViewEmailInBriefing={onViewEmailInBriefing}
                          onAction={isOutlook ? handleMailAction : undefined}
                          folders={mailbox.folders}
                        />
                      );
                    })()}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── pagination ── */}
      {(mailbox.isLive
        ? mailbox.livePage > 1 || mailbox.liveHasMore
        : totalPages > 1) && (
        <div className="flex shrink-0 items-center justify-between border-t border-zinc-800 px-4 py-2">
          <button
            onClick={prevPage}
            disabled={(mailbox.isLive ? mailbox.livePage : currentPage) === 1}
            className="rounded p-1 text-zinc-500 transition-colors hover:text-zinc-300 disabled:opacity-30"
          >
            <FiChevronDown className="h-3.5 w-3.5 rotate-90" />
          </button>
          <span className="text-[10px] font-medium text-zinc-500">
            {mailbox.isLive
              ? mailbox.livePage
              : `${currentPage} / ${totalPages}`}
          </span>
          <button
            onClick={nextPage}
            disabled={
              mailbox.isLive ? !mailbox.liveHasMore : currentPage === totalPages
            }
            className="rounded p-1 text-zinc-500 transition-colors hover:text-zinc-300 disabled:opacity-30"
          >
            <FiChevronDown className="h-3.5 w-3.5 -rotate-90" />
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Meetings Panel ───────────────────────────────────────────────────────────

function MeetingsPanel({ onStageChat }) {
  const [expanded, setExpanded] = useState(null);
  const [prepMeeting, setPrepMeeting] = useState(null);
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);

  const { userGoogleId } = DataState();
  const { isOutlook, isOutlookAuthenticated, isGmailAuthenticated } =
    useEmails();

  const isConnected = isOutlook ? isOutlookAuthenticated : isGmailAuthenticated;

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const fetcher = isOutlook
        ? fetchOutlookCalendarEvents
        : fetchGoogleCalendarEvents;
      const response = await fetcher(userGoogleId, 7);
      if (response.data) {
        setMeetings(response.data);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch calendar events:', error);
    } finally {
      setLoading(false);
    }
  }, [userGoogleId, isOutlook]);

  useEffect(() => {
    if (!userGoogleId || !isConnected) {
      setLoading(false);
      return;
    }
    fetchEvents();
  }, [userGoogleId, isConnected, fetchEvents]);

  if (!isConnected) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center">
        <FiVideo className="h-7 w-7 text-zinc-600" />
        <p className="text-xs text-zinc-500">
          Connect your {isOutlook ? 'Outlook' : 'Google'} account to view
          upcoming meetings.
        </p>
      </div>
    );
  }

  return (
    <div className="sc relative flex flex-1 flex-col overflow-y-auto">
      <div className="flex justify-end p-2 pb-0">
        <button
          onClick={fetchEvents}
          disabled={loading}
          className="rounded p-1 text-zinc-500 transition-colors hover:bg-zinc-800 hover:text-zinc-300 disabled:opacity-50"
          title="Resync meetings"
        >
          <FiRefreshCw
            className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`}
          />
        </button>
      </div>

      {loading ? (
        <div className="sc flex-1 overflow-y-auto p-4 pt-2">
          <div className="divide-y divide-zinc-800/50">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="flex animate-pulse items-start justify-between gap-2 py-4"
              >
                <div className="flex w-full items-start gap-2.5">
                  <div className="h-3.5 w-3.5 shrink-0 rounded-full bg-zinc-800" />
                  <div className="w-full space-y-2">
                    <div className="h-3 w-3/4 rounded bg-zinc-800" />
                    <div className="h-2.5 w-1/4 rounded bg-zinc-800/60" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : meetings.length === 0 ? (
        <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center">
          <FiVideo className="h-7 w-7 text-zinc-600" />
          <p className="text-xs text-zinc-500">
            No upcoming meetings in the next 7 days.
          </p>
        </div>
      ) : (
        <div className="divide-y divide-zinc-800/50">
          {meetings.map((m) => {
            // Format the datetime natively for display
            const dateObj = new Date(m.start_time);
            const timeString = isNaN(dateObj.getTime())
              ? m.start_time // Fallback if format is weird
              : dateObj.toLocaleDateString('en-US', {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                });

            return (
              <div key={m.id}>
                <button
                  onClick={() => setExpanded((p) => (p === m.id ? null : m.id))}
                  className="w-full px-4 py-3 text-left transition-colors hover:bg-zinc-800/60"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex min-w-0 items-start gap-2.5">
                      <FiVideo className="mt-0.5 h-3.5 w-3.5 shrink-0 text-zinc-500" />
                      <div className="min-w-0">
                        <p className="truncate text-xs font-medium leading-snug text-zinc-200">
                          {m.title}
                        </p>
                        <p className="mt-0.5 text-[10px] text-zinc-500">
                          {timeString}
                        </p>
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-1.5">
                      {m.attendees && m.attendees.length > 0 && (
                        <div className="flex -space-x-1.5">
                          {m.attendees.slice(0, 3).map((a, i) => (
                            <Av
                              key={i}
                              initials={a.charAt(0).toUpperCase()}
                              color={
                                [
                                  'bg-violet-600',
                                  'bg-blue-600',
                                  'bg-emerald-600',
                                  'bg-amber-600',
                                  'bg-rose-600',
                                  'bg-cyan-600',
                                ][i % 6]
                              }
                            />
                          ))}
                          {m.attendees.length > 3 && (
                            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-zinc-900 bg-zinc-700 text-[9px] font-semibold text-zinc-300">
                              +{m.attendees.length - 3}
                            </span>
                          )}
                        </div>
                      )}
                      <FiChevronDown
                        className={`h-3.5 w-3.5 text-zinc-500 transition-transform duration-200 ${expanded === m.id ? 'rotate-180' : ''}`}
                      />
                    </div>
                  </div>
                </button>
                {expanded === m.id && (
                  <div className="animate-fi space-y-3 px-4 pb-4">
                    {onStageChat && (
                      <ChatWithAgentButton
                        onClick={() =>
                          stageItemForAgent(
                            onStageChat,
                            'calendar_meeting',
                            m.title,
                            m.id,
                            {
                              title: m.title,
                              start_time: m.start_time,
                              attendees: m.attendees,
                              summary: m.summary,
                              meet_link: m.meet_link,
                            },
                          )
                        }
                      />
                    )}
                    {m.summary && (
                      <div className="rounded-lg bg-zinc-800/60 p-3">
                        <Label>Meeting Summary</Label>
                        <p className="mt-1.5 text-xs leading-relaxed text-zinc-300">
                          {m.summary}
                        </p>
                      </div>
                    )}
                    {m.prep && (
                      <div className="flex items-center justify-between">
                        <Label>Meeting Prep</Label>
                        <button
                          onClick={() => setPrepMeeting(m)}
                          className="rounded-full border border-zinc-600 px-3 py-1 text-[10px] font-medium text-zinc-300 transition-colors hover:bg-zinc-700 hover:text-zinc-100"
                        >
                          Open
                        </button>
                      </div>
                    )}
                    {m.attendees && m.attendees.length > 0 && (
                      <div className="space-y-1.5">
                        {m.attendees.map((e, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <Av
                              initials={e.charAt(0).toUpperCase()}
                              color={
                                [
                                  'bg-violet-600',
                                  'bg-blue-600',
                                  'bg-emerald-600',
                                  'bg-amber-600',
                                  'bg-rose-600',
                                  'bg-cyan-600',
                                ][i % 6]
                              }
                            />
                            <span className="truncate text-[11px] text-zinc-400">
                              {e}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                    {m.meet_link && (
                      <a
                        href={m.meet_link}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-2 inline-flex items-center gap-2 rounded-lg bg-blue-600/20 px-4 py-2 text-xs font-semibold text-blue-400 transition-colors hover:bg-blue-600/30"
                      >
                        <FiVideo className="h-3.5 w-3.5" /> Join Meeting
                      </a>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {prepMeeting && (
        <PrepModal meeting={prepMeeting} onClose={() => setPrepMeeting(null)} />
      )}
    </div>
  );
}

// ─── CR Todo Item ─────────────────────────────────────────────────────────────

const CR_SOURCE_STYLE = {
  email: {
    label: 'Email',
    cls: 'bg-blue-500/20 text-blue-400 border border-blue-500/20',
  },
  chat: {
    label: 'Chat',
    cls: 'bg-green-500/20 text-green-400 border border-green-500/20',
  },
  meeting: {
    label: 'Meeting',
    cls: 'bg-purple-500/20 text-purple-400 border border-purple-500/20',
  },
};

function CRTodoItem({ cr, onApprove, onReject, onOpen }) {
  const src = CR_SOURCE_STYLE[cr.source_origin] || {
    label: cr.source_origin || 'Unknown',
    cls: 'bg-zinc-700 text-zinc-400',
  };
  return (
    <div className="px-3 py-2.5">
      <div className="rounded-xl border border-amber-500/25 bg-amber-500/5 shadow-[inset_0_0_0_1px_rgba(251,191,36,0.08)]">
        <div className="px-3 pb-2 pt-3">
          <div className="mb-1.5 flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <span className="font-mono text-[10px] font-bold text-amber-400">
                {cr.id}
              </span>
              <span
                className={`rounded px-1.5 py-0.5 text-[9px] font-semibold ${src.cls}`}
              >
                {src.label}
              </span>
            </div>
            <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-widest text-amber-400">
              Change Request
            </span>
          </div>
          <button
            onClick={onOpen}
            className="mb-0.5 text-left text-xs font-semibold leading-snug text-zinc-100 transition-colors hover:text-white"
          >
            {cr.title}
          </button>
          <p className="mb-2.5 text-[11px] text-zinc-500">
            {cr.raisedBy} · {cr.date}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={onOpen}
              className="rounded-lg border border-amber-500/20 bg-amber-500/10 px-3 py-1.5 text-[11px] font-semibold text-amber-300 transition-colors hover:bg-amber-500/20"
            >
              View
            </button>
            <button
              onClick={onApprove}
              className="flex-1 rounded-lg border border-emerald-600/40 bg-emerald-600/20 py-1.5 text-[11px] font-semibold text-emerald-400 transition-colors hover:bg-emerald-600/30"
            >
              Approve
            </button>
            <button
              onClick={onReject}
              className="rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-1.5 text-[11px] font-semibold text-red-400 transition-colors hover:bg-red-500/20"
            >
              Reject
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Todo Panel ───────────────────────────────────────────────────────────────

function TodoPanel({ compact = false, onCrApproved, onStageChat }) {
  const { pendingCRs, approveChangeRequest, rejectChangeRequest, openCRModal } =
    useChangeRequests();
  const { userGoogleId: accessToken } = DataState();
  const { isTodoAuthenticated } = useEmails();

  const [todos, setTodos] = useState([]);
  const [internalTodos, setInternalTodos] = useState([]);
  const [msTodos, setMsTodos] = useState([]);
  const [msLists, setMsLists] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const newTodoInputRef = useRef(null);

  // New Task States
  const [newText, setNewText] = useState('');
  const [newNote, setNewNote] = useState('');
  const [selectedListId, setSelectedListId] = useState('internal');
  const [newTaskImportance, setNewTaskImportance] = useState('normal');
  const [newTaskDueDate, setNewTaskDueDate] = useState('');

  // New Group States
  const [isAddingGroup, setIsAddingGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');

  // Fetch all tasks and combine
  const fetchTodos = useCallback(() => {
    if (!accessToken) return;
    setIsLoading(true);

    const fetchInternal = getTodos(accessToken)
      .then((res) => {
        if (res && res.data) {
          setInternalTodos(
            res.data.map((t) => ({
              ...t,
              source: 'internal',
              importance: t.is_important ? 'high' : 'normal',
              dueDate: t.due_date || null,
            })),
          );
        }
      })
      // eslint-disable-next-line no-console
      .catch((err) => console.error('Failed to fetch internal todos:', err));

    let fetchMs = Promise.resolve();
    if (isTodoAuthenticated) {
      // One request for every list and its tasks (it was one per list).
      fetchMs = getAllMsTodo(accessToken)
        .then((res) => {
          if (!res?.data) return;
          setMsLists(res.data.lists || []);
          setMsTodos(
            (res.data.tasks || []).map((t) => ({
              id: t.id,
              listId: t.listId,
              listName: t.listName,
              title: t.title,
              completed: t.status === 'completed',
              note: t.body?.content || '',
              importance: t.importance || 'normal',
              dueDate: t.dueDateTime?.dateTime || null,
              source: 'mstodo',
            })),
          );
        })
        // eslint-disable-next-line no-console
        .catch((err) => console.error('Failed to fetch MS Todo tasks:', err));
    }

    Promise.all([fetchInternal, fetchMs]).finally(() => setIsLoading(false));
  }, [accessToken, isTodoAuthenticated]);

  useEffect(() => {
    fetchTodos();
  }, [fetchTodos]);

  useEffect(() => {
    setTodos([...internalTodos, ...msTodos]);
  }, [internalTodos, msTodos]);

  const toggle = async (id) => {
    const todo = todos.find((t) => t.id === id);
    if (!todo) return;

    // Optimistic UI update
    setTodos((p) =>
      p.map((t) => (t.id === id ? { ...t, completed: !t.completed } : t)),
    );
    if (todo.source === 'internal') {
      setInternalTodos((p) =>
        p.map((t) => (t.id === id ? { ...t, completed: !t.completed } : t)),
      );
    } else {
      setMsTodos((p) =>
        p.map((t) => (t.id === id ? { ...t, completed: !t.completed } : t)),
      );
    }

    try {
      if (todo.source === 'internal') {
        await updateTodo(accessToken, id, { completed: !todo.completed });
      } else {
        await updateMsTodoTask(accessToken, todo.listId, id, {
          status: !todo.completed ? 'completed' : 'notStarted',
        });
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to toggle todo:', e);
      // Revert optimistic UI on failure
      if (todo.source === 'internal') {
        setInternalTodos((p) =>
          p.map((t) => (t.id === id ? { ...t, completed: todo.completed } : t)),
        );
      } else {
        setMsTodos((p) =>
          p.map((t) => (t.id === id ? { ...t, completed: todo.completed } : t)),
        );
      }
    }
  };

  const addNewGroup = async () => {
    if (!newGroupName.trim() || !accessToken || !isTodoAuthenticated) return;
    try {
      const res = await createMsTodoList(accessToken, newGroupName.trim());
      if (res.data) {
        setMsLists((p) => [...p, res.data]);
        toast.success('Group created successfully');
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to create group:', e);
      toast.error('Failed to create group');
    } finally {
      setIsAddingGroup(false);
      setNewGroupName('');
    }
  };

  const addNew = async () => {
    const rawTitle = newText.trim() || newNote.trim();
    if (!rawTitle) {
      toast.error('Please enter a task title');
      return;
    }

    const textToSave = rawTitle;
    const noteToSave = newText.trim() ? newNote.trim() : '';
    const importanceToSave = newTaskImportance;
    const dueDateToSave = newTaskDueDate;
    const listIdToSave = selectedListId;

    const todoData = {
      title: textToSave,
      note: noteToSave || undefined,
      due_date: dueDateToSave ? new Date(dueDateToSave).toISOString() : null,
      is_important: importanceToSave === 'high',
    };

    const tempId = 'temp-' + Date.now();

    setNewText('');
    setNewNote('');
    setNewTaskImportance('normal');
    setNewTaskDueDate('');
    setIsAdding(false);
    setTimeout(() => newTodoInputRef.current?.focus(), 50);

    if (listIdToSave === 'internal') {
      const newTodo = {
        id: tempId,
        title: textToSave,
        note: noteToSave,
        completed: false,
        importance: importanceToSave,
        dueDate: dueDateToSave || null,
        source: 'internal',
      };
      setInternalTodos((p) => [newTodo, ...p]);

      try {
        const res = await addTodo(accessToken, todoData);
        if (res && res.data) {
          setInternalTodos((p) =>
            p.map((t) =>
              t.id === tempId
                ? {
                    ...res.data,
                    source: 'internal',
                    importance: res.data.is_important ? 'high' : 'normal',
                    dueDate: res.data.due_date || null,
                  }
                : t,
            ),
          );
          toast.success('Task added');
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to add todo:', e);
        toast.error(
          typeof e === 'string' ? e : e?.message || 'Failed to add todo',
        );
        setInternalTodos((p) => p.filter((t) => t.id !== tempId));
      }
    } else {
      // MS Todo
      const selectedList = msLists.find((l) => l.id === listIdToSave);
      const newTodo = {
        id: tempId,
        listId: listIdToSave,
        listName: selectedList?.displayName || 'Microsoft Todo',
        title: textToSave,
        note: noteToSave,
        completed: false,
        importance: importanceToSave,
        dueDate: dueDateToSave || null,
        source: 'mstodo',
      };
      setMsTodos((p) => [newTodo, ...p]);

      try {
        const msTodoData = {
          title: textToSave,
          body: { content: noteToSave, contentType: 'text' },
          importance: importanceToSave,
        };
        if (dueDateToSave) {
          msTodoData.dueDateTime = {
            dateTime: new Date(dueDateToSave).toISOString(),
            timeZone: 'UTC',
          };
        }
        const res = await addMsTodoTask(accessToken, listIdToSave, msTodoData);
        if (res && res.data) {
          setMsTodos((p) =>
            p.map((t) =>
              t.id === tempId
                ? {
                    id: res.data.id,
                    listId: listIdToSave,
                    listName: selectedList?.displayName || 'Microsoft Todo',
                    title: res.data.title,
                    completed: res.data.status === 'completed',
                    note: res.data.body?.content || '',
                    importance: res.data.importance || 'normal',
                    dueDate: res.data.dueDateTime?.dateTime || null,
                    source: 'mstodo',
                  }
                : t,
            ),
          );
          toast.success('Task added to Microsoft To Do');
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to add MS todo:', e);
        toast.error(
          typeof e === 'string' ? e : e?.message || 'Failed to add MS todo',
        );
        setMsTodos((p) => p.filter((t) => t.id !== tempId));
      }
    }
  };

  const addSug = async (s) => {
    const todoData = {
      title: s.text,
      note: '',
    };
    const tempId = 'temp-' + Date.now();
    const newTodo = {
      id: tempId,
      ...todoData,
      completed: false,
      source: 'internal',
    };
    setInternalTodos((p) => [newTodo, ...p]);

    try {
      const res = await addTodo(accessToken, todoData);
      if (res.data) {
        setInternalTodos((p) =>
          p.map((t) =>
            t.id === tempId ? { ...res.data, source: 'internal' } : t,
          ),
        );
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to add suggestion:', e);
      setInternalTodos((p) => p.filter((t) => t.id !== tempId));
    }
  };

  const handleDelete = async (id) => {
    const todo = todos.find((t) => t.id === id);
    if (!todo) return;

    if (todo.source === 'internal') {
      setInternalTodos((p) => p.filter((t) => t.id !== id));
      try {
        await deleteTodo(accessToken, id);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to delete todo:', e);
        setInternalTodos((p) => [...p, todo]);
      }
    } else {
      setMsTodos((p) => p.filter((t) => t.id !== id));
      try {
        await deleteMsTodoTask(accessToken, todo.listId, id);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to delete MS todo:', e);
        setMsTodos((p) => [...p, todo]);
      }
    }
  };

  const cancelAdding = () => {
    setIsAdding(false);
    setNewText('');
    setNewNote('');
    setNewTaskImportance('normal');
    setNewTaskDueDate('');
    newTodoInputRef.current?.blur();
    if (
      document.activeElement &&
      typeof document.activeElement.blur === 'function'
    ) {
      document.activeElement.blur();
    }
  };

  const handleApproveCR = (cr) => {
    approveChangeRequest(cr.id);
    onCrApproved?.(cr);
  };

  return (
    <div className="sc flex h-full min-h-0 flex-col overflow-y-auto">
      {/* To Do Header */}
      <div className="flex items-center justify-between border-b border-zinc-700/60 bg-zinc-800/20 px-6 py-2.5">
        <div className="flex items-center gap-2">
          <FiCheck className="h-3.5 w-3.5 text-zinc-400" />
          <span className="text-xs font-semibold uppercase tracking-wider text-zinc-300">
            To Do
          </span>
        </div>
        <button
          onClick={fetchTodos}
          disabled={isLoading}
          className="rounded-lg p-1.5 text-zinc-500 transition-colors hover:bg-zinc-800 hover:text-zinc-300 disabled:opacity-50"
          title="Resync tasks"
        >
          <FiRefreshCw
            className={`h-3.5 w-3.5 ${isLoading ? 'animate-spin' : ''}`}
          />
        </button>
      </div>

      {/* Pending Change Requests */}
      {pendingCRs.length > 0 && (
        <div className="border-b border-zinc-700/40 px-3 py-2">
          <div className="flex items-center gap-2 px-3 py-1.5">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-amber-400/70">
              Pending Approval
            </span>
            <span className="flex h-4 w-4 items-center justify-center rounded-full bg-amber-500 text-[9px] font-bold text-black">
              {pendingCRs.length}
            </span>
          </div>
          {pendingCRs.map((cr) => (
            <CRTodoItem
              key={cr.id}
              cr={cr}
              onOpen={() => openCRModal(cr)}
              onApprove={() => handleApproveCR(cr)}
              onReject={() => rejectChangeRequest(cr.id)}
            />
          ))}
        </div>
      )}

      {/* Todos */}
      <div className="flex flex-col pb-4">
        {isLoading ? (
          <div className="flex h-20 items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-zinc-500" />
          </div>
        ) : todos.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center text-zinc-500">
            <ListTodo className="mb-2 h-8 w-8 opacity-20" />
            <p className="text-[11px] font-medium tracking-wide">
              No tasks left.
            </p>
          </div>
        ) : (
          Object.entries(
            todos.reduce((acc, t) => {
              const groupName =
                t.source === 'internal'
                  ? 'General'
                  : t.listName || 'Microsoft Todo';
              if (!acc[groupName]) acc[groupName] = [];
              acc[groupName].push(t);
              return acc;
            }, {}),
          ).map(([groupName, groupTasks], groupIndex) => (
            <div key={groupName} className="flex flex-col">
              {groupIndex > 0 && (
                <div className="mx-6 my-2 border-t border-zinc-700/40" />
              )}
              <div className="px-6 pb-1 pt-3">
                <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                  {groupName}
                </span>
              </div>
              <div className="flex flex-col gap-0.5 px-3">
                {groupTasks
                  .sort((a, b) => {
                    // Sort by date first (earliest first)
                    if (a.dueDate && b.dueDate) {
                      const dateA = new Date(a.dueDate).getTime();
                      const dateB = new Date(b.dueDate).getTime();
                      if (dateA !== dateB) return dateA - dateB;
                    } else if (a.dueDate) {
                      return -1;
                    } else if (b.dueDate) {
                      return 1;
                    }

                    // Dates are equal or both lack date, sort by importance
                    const impA = a.importance === 'high' ? 1 : 0;
                    const impB = b.importance === 'high' ? 1 : 0;
                    if (impA !== impB) return impB - impA;

                    // Lastly sort by completed status (uncompleted first)
                    return a.completed === b.completed
                      ? 0
                      : a.completed
                        ? 1
                        : -1;
                  })
                  .map((t) => (
                    <div
                      key={t.id}
                      className={`group flex items-start gap-3 rounded-lg px-3 py-2 transition-colors hover:bg-zinc-800/40 ${t.completed ? 'opacity-60' : ''}`}
                    >
                      <button
                        onClick={() => toggle(t.id)}
                        className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-all
                  ${t.completed ? 'border-transparent bg-white/20 text-white' : 'border-zinc-500 hover:border-zinc-300'}`}
                      >
                        {t.completed && (
                          <FiCheck className="h-3 w-3" strokeWidth={3} />
                        )}
                      </button>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          {t.importance === 'high' && (
                            <span
                              className="flex h-3 w-3 items-center justify-center rounded-full bg-amber-500/20 text-[9px] font-bold text-amber-500"
                              title="High Importance"
                            >
                              !
                            </span>
                          )}
                          <p
                            className={`truncate text-xs font-medium ${t.completed ? 'text-zinc-500 line-through' : 'text-zinc-200'}`}
                          >
                            {t.title}
                          </p>
                          {t.dueDate && (
                            <span className="shrink-0 text-[9px] font-medium text-amber-400/80">
                              {new Date(t.dueDate).toLocaleDateString(
                                undefined,
                                { month: 'short', day: 'numeric' },
                              )}
                            </span>
                          )}
                        </div>
                        {!compact && t.note && (
                          <p className="mt-0.5 line-clamp-2 text-[11px] text-zinc-400">
                            {t.note}
                          </p>
                        )}
                      </div>
                      {onStageChat && (
                        <ChatWithAgentButton
                          compact
                          onClick={() =>
                            stageItemForAgent(
                              onStageChat,
                              'todo',
                              t.title,
                              t.id,
                              {
                                title: t.title,
                                note: t.note,
                                dueDate: t.dueDate,
                                importance: t.importance,
                                completed: t.completed,
                                list: t.listName || t.source,
                              },
                            )
                          }
                        />
                      )}
                      <button
                        onClick={() => handleDelete(t.id)}
                        className="mt-0.5 text-zinc-600 opacity-0 transition-opacity hover:text-red-400 group-hover:opacity-100"
                        title="Delete task"
                      >
                        <FiX className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Divider above Add Todo */}
      <div className="mx-6 my-2 border-t border-zinc-700/40" />

      {/* Add new todo */}
      <div className="mx-3 flex items-start gap-3 rounded-lg px-3 py-2 transition-colors hover:bg-zinc-800/20">
        <div className="mt-0.5 h-4 w-4 shrink-0 rounded border border-zinc-600 opacity-50" />
        <div className="flex min-w-0 flex-1 flex-col">
          <input
            ref={newTodoInputRef}
            value={newText}
            onChange={(e) => setNewText(e.target.value)}
            onFocus={() => setIsAdding(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') addNew();
              if (e.key === 'Escape') cancelAdding();
            }}
            placeholder="Add new to do"
            className="w-full bg-transparent text-xs font-medium text-zinc-200 placeholder-zinc-500 outline-none"
          />
          {isAdding && newText.trim() && (
            <div className="mt-1 flex items-center gap-1.5">
              <span className="flex items-center gap-1 text-[10px] text-zinc-600">
                <kbd className="inline-flex items-center justify-center rounded bg-zinc-800 px-1 py-0.5 text-[9px] font-medium text-zinc-400">
                  ↵
                </kbd>
                to save
              </span>
              <span className="text-[10px] text-zinc-700">·</span>
              <span className="flex items-center gap-1 text-[10px] text-zinc-600">
                <kbd className="inline-flex items-center justify-center rounded bg-zinc-800 px-1 py-0.5 text-[9px] font-medium text-zinc-400">
                  Esc
                </kbd>
                to cancel
              </span>
            </div>
          )}
          {isAdding && (
            <div className="mt-1 flex flex-col">
              <textarea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    addNew();
                  }
                  if (e.key === 'Escape') cancelAdding();
                }}
                placeholder="Notes"
                className="mt-1 h-12 w-full resize-none bg-transparent text-[11px] text-zinc-400 placeholder-zinc-600 outline-none"
              />
              <div className="mt-2 flex flex-wrap items-center justify-between gap-2 border-t border-zinc-700/40 pt-2">
                <div className="flex flex-wrap items-center gap-2">
                  {isTodoAuthenticated ? (
                    <select
                      value={selectedListId}
                      onChange={(e) => setSelectedListId(e.target.value)}
                      className="cursor-pointer rounded border border-zinc-700/50 bg-zinc-800/50 px-2 py-1 text-[10px] text-zinc-300 outline-none transition-colors hover:bg-zinc-700/50"
                    >
                      <option value="internal">General Tasks</option>
                      {msLists.map((list) => (
                        <option key={list.id} value={list.id}>
                          {list.displayName}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <button className="rounded bg-zinc-800 p-1.5 text-zinc-400 transition-colors hover:bg-zinc-700">
                      <VscReport className="h-3.5 w-3.5" />
                    </button>
                  )}

                  <button
                    onClick={() =>
                      setNewTaskImportance((prev) =>
                        prev === 'high' ? 'normal' : 'high',
                      )
                    }
                    className={`rounded p-1 transition-colors ${newTaskImportance === 'high' ? 'bg-amber-500/20 text-amber-500' : 'bg-zinc-800/50 text-zinc-500 hover:bg-zinc-700/50 hover:text-amber-500/70'}`}
                    title="Toggle High Importance"
                  >
                    <span className="px-1 text-[10px] font-bold">!</span>
                  </button>

                  <div className="relative flex items-center">
                    <input
                      type="date"
                      value={newTaskDueDate}
                      onChange={(e) => setNewTaskDueDate(e.target.value)}
                      className="cursor-pointer rounded border border-zinc-700/50 bg-zinc-800/50 px-2 py-1 text-[10px] text-zinc-400 outline-none transition-colors hover:bg-zinc-700/50"
                      style={{ colorScheme: 'dark' }}
                      title="Set Due Date"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={cancelAdding}
                    className="px-2 py-1 text-[11px] text-zinc-500 hover:text-zinc-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={addNew}
                    className="rounded bg-zinc-800 px-3 py-1 text-[11px] text-zinc-200 transition-colors hover:bg-zinc-700"
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add new group */}
      {isTodoAuthenticated && (
        <div className="mx-3 px-3 py-2">
          {!isAddingGroup ? (
            <button
              onClick={() => setIsAddingGroup(true)}
              className="flex w-full items-center gap-2 text-[11px] font-medium text-zinc-500 transition-colors hover:text-blue-400"
            >
              <FiPlus className="h-3.5 w-3.5" />
              <span>Add Group</span>
            </button>
          ) : (
            <div className="flex flex-col gap-2">
              <input
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') addNewGroup();
                  if (e.key === 'Escape') {
                    setIsAddingGroup(false);
                    setNewGroupName('');
                  }
                }}
                placeholder="Group Name"
                className="w-full rounded border border-zinc-700 bg-zinc-800 px-2 py-1 text-xs text-zinc-200 outline-none"
              />
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => {
                    setIsAddingGroup(false);
                    setNewGroupName('');
                  }}
                  className="px-2 py-1 text-[11px] text-zinc-500 hover:text-zinc-300"
                >
                  Cancel
                </button>
                <button
                  onClick={addNewGroup}
                  className="rounded bg-zinc-800 px-3 py-1 text-[11px] text-zinc-200 hover:bg-zinc-700"
                >
                  Create
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Suggestions */}
      {!compact && (
        <div className="mb-4 mt-2 px-4">
          <div className="mb-3 flex items-center gap-2">
            <span className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-widest text-zinc-500">
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="opacity-70"
              >
                <path d="M4 22h14a2 2 0 0 0 2-2V7.5L14.5 2H6a2 2 0 0 0-2 2v4" />
                <polyline points="14 2 14 8 20 8" />
                <path d="M2 15h10" />
                <path d="m9 18 3-3-3-3" />
              </svg>
              SUGGESTIONS
            </span>
          </div>
          <div className="flex flex-col">
            {SUGGESTIONS.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-6 text-center text-zinc-500">
                <ListTodo className="mb-2 h-6 w-6 opacity-20" />
                <p className="text-[11px] font-medium tracking-wide">
                  No suggestions yet.
                </p>
                <p className="mt-0.5 text-[10px] text-zinc-600">
                  New suggestions will appear here when available.
                </p>
              </div>
            ) : (
              SUGGESTIONS.map((s) => (
                // eslint-disable-next-line
                <div
                  key={s.id}
                  className="group flex cursor-pointer items-start gap-3 py-2"
                  onClick={() => addSug(s)}
                >
                  <div className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
                    <FiPlus className="h-3.5 w-3.5 text-zinc-600 transition-colors group-hover:text-zinc-300" />
                  </div>
                  <p className="text-xs font-medium leading-snug text-zinc-400 transition-colors group-hover:text-zinc-200">
                    {s.text}
                  </p>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Morning Briefing Email Detail View ────────────────────────────────────────

function MorningBriefingEmailDetailView({ email, onClose, isOutlook = false }) {
  const [showReplyAllPanel, setShowReplyAllPanel] = useState(false);
  const [showReply, setShowReply] = useState(false);
  const [showInsightsPanel, setShowInsightsPanel] = useState(false);
  // const [showActionsPanel, setShowActionsPanel] = useState(false); // hidden for now
  const [showForwardPanel, setShowForwardPanel] = useState(false);

  // reply state
  const [messageBody, setMessageBody] = useState('');
  const [customPrompt, setCustomPrompt] = useState('');
  const [localAttachments, setLocalAttachments] = useState([]);
  const [driveAttachments, setDriveAttachments] = useState([]);

  // forward state
  const [message, setMessage] = useState('');
  const [recepientsEmails, setRecepientsEmails] = useState([]);
  const [inputValue, setInputValue] = useState('');

  const { userInfo } = DataState();
  const loggedInEmail = userInfo?.email || '';
  const toList =
    email?.to
      ?.map((r) => {
        if (typeof r === 'string') return r;
        return (
          r?.contact ||
          r?.emailAddress?.address ||
          r?.emailAddress?.name ||
          r?.email ||
          r?.address ||
          r?.name ||
          'Unknown'
        );
      })
      .filter((r) => typeof r === 'string') || [];

  const ccList =
    email?.cc
      ?.map((r) => {
        if (typeof r === 'string') return r;
        return (
          r?.contact ||
          r?.emailAddress?.address ||
          r?.emailAddress?.name ||
          r?.email ||
          r?.address ||
          r?.name ||
          'Unknown'
        );
      })
      .filter((r) => typeof r === 'string') || [];

  const bccList =
    email?.bcc
      ?.map((r) => {
        if (typeof r === 'string') return r;
        return (
          r?.contact ||
          r?.emailAddress?.address ||
          r?.emailAddress?.name ||
          r?.email ||
          r?.address ||
          r?.name ||
          'Unknown'
        );
      })
      .filter((r) => typeof r === 'string') || [];

  const subject =
    typeof email?.subject === 'string' ? email.subject : '(no subject)';

  let from = 'Unknown';
  let fromEmail = '';
  if (email?.from) {
    let fromObj = email.from;
    // Some APIs wrap it in a `value` array
    if (fromObj.value && Array.isArray(fromObj.value)) {
      fromObj = fromObj.value[0];
    } else if (Array.isArray(fromObj)) {
      fromObj = fromObj[0];
    }

    if (typeof fromObj === 'string') {
      from = fromObj;
      fromEmail = fromObj; // Fallback to the same string
    } else if (fromObj) {
      from =
        fromObj.name ||
        fromObj.emailAddress?.name ||
        fromObj.address ||
        fromObj.emailAddress?.address ||
        fromObj.email ||
        'Unknown';
      fromEmail =
        fromObj.contact ||
        fromObj.emailAddress?.address ||
        fromObj.address ||
        fromObj.email ||
        '';
    }
  }
  // Ensure from is a string just in case
  from = typeof from === 'string' ? from : String(from);
  fromEmail = typeof fromEmail === 'string' ? fromEmail : String(fromEmail);

  const allParticipants = [
    ...new Set([fromEmail, ...toList, ...ccList, ...bccList]),
  ].filter(Boolean);
  const otherParticipants = allParticipants.filter(
    (r) =>
      typeof r === 'string' && r.toLowerCase() !== loggedInEmail?.toLowerCase(),
  );
  const showReplyAllBtn = otherParticipants.length > 1;

  const aiSummary = email?.summary || null;
  const date = email?.date
    ? new Date(email.date).toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';

  return (
    <div className="bg-1 relative flex h-full w-full flex-col overflow-hidden">
      <div className="relative z-10 shrink-0 border-b border-zinc-700 px-7 pb-5 pt-8">
        <button
          onClick={onClose}
          className="mb-5 flex items-center gap-2 text-xs font-medium text-zinc-400 transition-colors hover:text-zinc-200"
        >
          <FiArrowLeft className="h-3.5 w-3.5" />
          Back to Briefing
        </button>
        <h1 className="text-xl font-semibold leading-tight text-zinc-100">
          {subject}
        </h1>
        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#5652CF] text-xs font-semibold text-white">
              {(fromEmail === loggedInEmail ? 'Me' : from)[0]?.toUpperCase()}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium text-zinc-200">
                  {fromEmail === loggedInEmail ? 'Me' : from}
                </p>
                {fromEmail && fromEmail !== loggedInEmail && (
                  <p className="text-[11px] text-zinc-500">
                    &lt;{fromEmail}&gt;
                  </p>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-x-2 text-[11px] text-zinc-500">
                {toList.length > 0 && (
                  <p>
                    To: {toList.slice(0, 2).join(', ')}
                    {toList.length > 2 ? ` +${toList.length - 2}` : ''}
                  </p>
                )}
                {ccList.length > 0 && (
                  <>
                    <span className="text-zinc-700">•</span>
                    <p>
                      Cc: {ccList.slice(0, 2).join(', ')}
                      {ccList.length > 2 ? ` +${ccList.length - 2}` : ''}
                    </p>
                  </>
                )}
                {bccList.length > 0 && (
                  <>
                    <span className="text-zinc-700">•</span>
                    <p>
                      Bcc: {bccList.slice(0, 2).join(', ')}
                      {bccList.length > 2 ? ` +${bccList.length - 2}` : ''}
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[11px] text-zinc-500">{date}</span>
            <span
              className={`rounded-md px-2 py-0.5 text-[10px] font-semibold text-white ${email?.priority === 1 || email?.priority === 0 ? 'bg-blue-400' : email?.priority >= 2 && email?.priority <= 3 ? 'bg-yellow-400' : email?.priority >= 4 ? 'bg-red-400' : 'bg-zinc-500'}`}
            >
              {email?.priority === 1 || email?.priority === 0
                ? 'Low'
                : email?.priority >= 2 && email?.priority <= 3
                  ? 'Medium'
                  : email?.priority >= 4
                    ? 'High'
                    : 'Unknown'}
            </span>
          </div>
        </div>
      </div>

      <div className="sc relative z-10 min-h-0 flex-1 overflow-y-auto px-7 py-6">
        {/* AI Summary Section: only for analysed mail; the body below
            already shows the rest. */}
        {aiSummary && (
          <div className="mb-6 rounded-xl border border-zinc-700 bg-zinc-800/40 p-5 backdrop-blur-sm">
            <div className="mb-2.5 flex items-center gap-2">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-zinc-400">
                ✨ AI Summary
              </span>
            </div>
            <p className="text-sm leading-relaxed text-zinc-300">{aiSummary}</p>
            {/* Action Items — hidden for now
          {email?.actionItems?.length > 0 && (
            <div className="mt-4 border-t border-zinc-700/50 pt-3">
              <span className="mb-2 block text-[10px] font-semibold uppercase tracking-widest text-zinc-400">
                ⚡ Action Items
              </span>
              <div className="flex flex-wrap gap-2">
                {email.actionItems.map((action, i) => (
                  <span
                    key={i}
                    className="rounded bg-zinc-700/50 px-2.5 py-1 text-xs text-zinc-200"
                  >
                    {action?.task}
                  </span>
                ))}
              </div>
            </div>
          )}
          */}
          </div>
        )}

        {/* Email Body / Content */}
        <div className="overflow-hidden rounded-xl border border-zinc-700/60 shadow-sm">
          {/* Outlook sends an empty HTML shell for a blank mail: frame it
              only when it has text or an image. */}
          {/<img/i.test(email?.body || '') ||
          (email?.body || '')
            .replace(/<(style|script)[\s\S]*?<\/\1>|<[^>]*>|&nbsp;|\s/gi, '')
            .trim() ? (
            <EmailIframe emailBody={email.body} />
          ) : (
            <div className="bg-zinc-800/40 p-5">
              <p className="whitespace-pre-wrap text-xs leading-relaxed text-zinc-400">
                {email?.snippet ||
                  email?.bodyPreview ||
                  'No content available.'}
              </p>
            </div>
          )}
        </div>

        {/* Action Bar & Attachments */}
        <div className="mt-6 flex flex-col gap-4">
          {email?.attachments && email.attachments.length > 0 && (
            <div className="flex flex-wrap items-center gap-3 border-b border-zinc-800 pb-4">
              <div className="mb-1 flex w-full items-center gap-2">
                <FiPaperclip className="text-zinc-500" size={16} />
                <span className="text-xs font-medium text-zinc-400">
                  Attachments
                </span>
              </div>
              {email.attachments.map((att) => (
                <div key={att.attachmentId} className="rounded-lg bg-zinc-800">
                  <AttachmentItem
                    messageId={email?.message_id || email?.id}
                    attachment={att}
                    isOutlook={isOutlook}
                  />
                </div>
              ))}
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-2">
            <button
              className={`flex items-center rounded-md border px-3 py-1.5 text-xs font-medium transition-all ${
                showInsightsPanel
                  ? 'border-[#5652CF] bg-[#5652CF] text-white'
                  : 'border-zinc-700 bg-zinc-800/40 text-zinc-300 backdrop-blur-sm hover:bg-zinc-700/50'
              }`}
              onClick={() => {
                setShowInsightsPanel(true);
                // setShowActionsPanel(false); // hidden for now
                setShowReply(false);
                setShowReplyAllPanel(false);
                setShowForwardPanel(false);
              }}
            >
              Timeline
            </button>

            {/* Actions button — hidden for now
            <button
              className={`flex items-center rounded-md border px-3 py-1.5 text-xs font-medium transition-all ${
                showActionsPanel
                  ? 'border-[#5652CF] bg-[#5652CF] text-white'
                  : 'border-zinc-700 bg-zinc-800/40 text-zinc-300 backdrop-blur-sm hover:bg-zinc-700/50'
              }`}
              onClick={() => {
                setShowInsightsPanel(false);
                setShowActionsPanel(true);
                setShowReply(false);
                setShowReplyAllPanel(false);
                setShowForwardPanel(false);
              }}
            >
              Actions
            </button>
            */}
            <button
              className={`flex items-center rounded-md border px-3 py-1.5 text-xs font-medium transition-all ${
                showReply
                  ? 'border-[#5652CF] bg-[#5652CF] text-white'
                  : 'border-zinc-700 bg-zinc-800/40 text-zinc-300 backdrop-blur-sm hover:bg-zinc-700/50'
              }`}
              onClick={() => {
                setShowInsightsPanel(false);
                // setShowActionsPanel(false); // hidden for now
                setShowReply(true);
                setShowReplyAllPanel(false);
                setShowForwardPanel(false);
              }}
            >
              <LuReply size={14} className="mr-1.5" /> Reply
            </button>
            {showReplyAllBtn && (
              <button
                className={`flex items-center rounded-md border px-3 py-1.5 text-xs font-medium transition-all ${
                  showReplyAllPanel
                    ? 'border-[#5652CF] bg-[#5652CF] text-white'
                    : 'border-zinc-700 bg-zinc-800/40 text-zinc-300 backdrop-blur-sm hover:bg-zinc-700/50'
                }`}
                onClick={() => {
                  setShowInsightsPanel(false);
                  // setShowActionsPanel(false); // hidden for now
                  setShowReply(false);
                  setShowReplyAllPanel(true);
                  setShowForwardPanel(false);
                }}
              >
                <LuReply size={14} className="mr-1.5" /> Reply All
              </button>
            )}
            <button
              className={`flex items-center rounded-md border px-3 py-1.5 text-xs font-medium transition-all ${
                showForwardPanel
                  ? 'border-[#5652CF] bg-[#5652CF] text-white'
                  : 'border-zinc-700 bg-zinc-800/40 text-zinc-300 backdrop-blur-sm hover:bg-zinc-700/50'
              }`}
              onClick={() => {
                setShowInsightsPanel(false);
                // setShowActionsPanel(false); // hidden for now
                setShowReply(false);
                setShowReplyAllPanel(false);
                setShowForwardPanel(true);
              }}
            >
              <LuForward size={14} className="mr-1.5" /> Forward
            </button>
          </div>

          {/* Panels Container */}
          <div className="mt-2 overflow-hidden rounded-xl border border-zinc-700 bg-zinc-800/40 backdrop-blur-sm transition-all">
            {showReply && (
              <BriefingReplyEmail
                sender={
                  fromEmail === loggedInEmail && toList.length > 0
                    ? [toList[0]]
                    : [fromEmail]
                }
                message_id={email?.message_id || email?.id}
                onClose={() => setShowReply(false)}
                messageBody={messageBody}
                setMessageBody={setMessageBody}
                localAttachments={localAttachments}
                driveAttachments={driveAttachments}
                setLocalAttachments={setLocalAttachments}
                setDriveAttachments={setDriveAttachments}
                customPrompt={customPrompt}
                setCustomPrompt={setCustomPrompt}
                isOutlook={isOutlook}
              />
            )}
            {showReplyAllPanel && (
              <BriefingReplyEmail
                sender={[fromEmail, ...toList, ...ccList, ...bccList]
                  .filter(Boolean)
                  .filter(
                    (e) => e.toLowerCase() !== loggedInEmail?.toLowerCase(),
                  )}
                message_id={email?.message_id || email?.id}
                onClose={() => setShowReplyAllPanel(false)}
                setShowReplyAllPanel={setShowReplyAllPanel}
                messageBody={messageBody}
                setMessageBody={setMessageBody}
                localAttachments={localAttachments}
                driveAttachments={driveAttachments}
                setLocalAttachments={setLocalAttachments}
                setDriveAttachments={setDriveAttachments}
                customPrompt={customPrompt}
                setCustomPrompt={setCustomPrompt}
                isOutlook={isOutlook}
              />
            )}
            {showInsightsPanel && (
              <BriefingTimelinePanel
                conversationId={email?.conversationId}
                isOutlook={isOutlook}
                fallbackEmail={email}
              />
            )}
            {/* Actions panel — hidden for now
            {showActionsPanel && (
              <BriefingActionsPanel
                messageId={email?.message_id || email?.id}
                isOutlook={isOutlook}
                actionItems={email?.actionItems || []}
              />
            )}
            */}
            {showForwardPanel && (
              <BriefingForwardEmail
                message_id={email?.message_id || email?.id}
                setShowForwardPanel={setShowForwardPanel}
                message={message}
                setMessage={setMessage}
                recepientsEmails={recepientsEmails}
                setRecepientsEmails={setRecepientsEmails}
                inputValue={inputValue}
                setInputValue={setInputValue}
                isOutlook={isOutlook}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Morning Briefing view
// ─────────────────────────────────────────────────────────────────────────────

function MorningBriefingView({
  onClose,
  onStageChat,
  briefingExpandedEmailId,
  setBriefingExpandedEmailId,
}) {
  const [activeTab, setActiveTab] = useState('emails');
  const [expandedBriefingEmail, setExpandedBriefingEmail] = useState(null);
  const { isOutlook } = useEmails();
  const { userInfo, userGoogleId: accessToken } = DataState();

  const userName = userInfo?.name?.split(' ')[0] || 'User';
  const todayFormatted = new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
    .format(new Date())
    .toUpperCase();

  const [weatherData, setWeatherData] = useState(null);
  const [weatherStatus, setWeatherStatus] = useState('idle');

  const fetchWeather = () => {
    setWeatherStatus('loading');
    if (!navigator.geolocation) {
      setWeatherStatus('error');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          const data = await getWeather(accessToken, latitude, longitude);

          if (data && data.weather) {
            const temp = Math.round(data.weather.current_weather.temperature);
            const code = data.weather.current_weather.weathercode;
            const descriptions = {
              0: 'Clear sky',
              1: 'Mainly clear',
              2: 'Partly cloudy',
              3: 'Overcast',
              45: 'Fog',
              48: 'Depositing rime fog',
              51: 'Light drizzle',
              53: 'Moderate drizzle',
              55: 'Dense drizzle',
              61: 'Slight rain',
              63: 'Moderate rain',
              65: 'Heavy rain',
              71: 'Slight snow',
              73: 'Moderate snow',
              75: 'Heavy snow',
              95: 'Thunderstorm',
            };
            const desc = descriptions[code] || 'Clear';
            const isDay =
              typeof data.weather.current_weather.is_day === 'number'
                ? data.weather.current_weather.is_day === 1
                : new Date().getHours() >= 6 && new Date().getHours() < 18;
            setWeatherData({
              temp,
              desc,
              location: data.city || 'Local Area',
              code,
              isDay,
            });
            setWeatherStatus('success');
          } else {
            setWeatherStatus('error');
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('Weather fetch failed', e);
          setWeatherStatus('error');
        }
      },
      (error) => {
        if (error.code === error.PERMISSION_DENIED) {
          setWeatherStatus('denied');
        } else {
          setWeatherStatus('error');
        }
      },
    );
  };

  useEffect(() => {
    fetchWeather();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const weatherVideoSrc =
    weatherStatus === 'success' && weatherData
      ? getWeatherVideoSrc(weatherData.code, weatherData.isDay)
      : null;

  const briefingTabs = {
    emails: { icon: <FiMail className="h-3.5 w-3.5" />, label: 'Emails' },
    ...(ENABLE_MEETINGS
      ? {
          meetings: {
            icon: <FiVideo className="h-3.5 w-3.5" />,
            label: 'Meetings',
          },
        }
      : {}),
  };

  return (
    <div className="animate-si relative flex h-full w-full gap-2 overflow-hidden">
      {/* Shared weather video — spans both cards */}
      {weatherVideoSrc && (
        <VideoBackground
          src={weatherVideoSrc}
          className="z-0 rounded-xl md:rounded-2xl"
        />
      )}

      {/* LEFT CARD: greeting + todo OR expanded email detail */}
      {expandedBriefingEmail ? (
        <div className="bg-1 relative z-10 flex h-full w-[58%] min-w-0 flex-col overflow-hidden rounded-xl border border-zinc-700 md:rounded-2xl">
          <MorningBriefingEmailDetailView
            email={expandedBriefingEmail}
            isOutlook={isOutlook}
            onClose={() => {
              setExpandedBriefingEmail(null);
              setBriefingExpandedEmailId?.(null);
            }}
          />
        </div>
      ) : (
        <div
          className={`relative z-10 flex h-full w-[58%] min-w-0 flex-col overflow-hidden rounded-xl border border-zinc-700 md:rounded-2xl ${
            weatherVideoSrc ? 'bg-black/60 backdrop-blur-md' : 'bg-1'
          }`}
        >
          {/* hero */}
          <div
            className={`relative z-10 shrink-0 border-b border-zinc-700 px-7 pb-6 pt-8 ${weatherVideoSrc ? '[text-shadow:0_1px_6px_rgba(0,0,0,0.85)]' : ''}`}
          >
            <p className="mb-3 flex items-center gap-2 text-[11px] font-medium tracking-wide text-zinc-400">
              <span>{todayFormatted}</span>
              {weatherStatus === 'loading' && (
                <span>• Fetching weather...</span>
              )}
              {weatherStatus === 'success' && weatherData && (
                <span>
                  • {weatherData.location.toUpperCase()} {weatherData.temp}° •{' '}
                  {weatherData.desc}
                </span>
              )}
              {weatherStatus === 'denied' && (
                <button
                  onClick={() =>
                    toast.error(
                      'Please allow location access in your browser settings (usually the lock icon in the address bar) to see local weather, then refresh the page.',
                    )
                  }
                  className="cursor-pointer transition-colors hover:text-zinc-300"
                >
                  • 📍 Location Denied (Click for info)
                </button>
              )}
              {weatherStatus === 'error' && (
                <button
                  onClick={fetchWeather}
                  className="cursor-pointer transition-colors hover:text-zinc-300"
                >
                  • ⚠️ Weather unavailable (Click to retry)
                </button>
              )}
            </p>
            <h1 className="text-[1.6rem] font-semibold leading-[1.3] text-zinc-100">
              Good morning,{' '}
              <span className="font-bold text-white">{userName}.</span>
            </h1>
            <p className="mt-0.5 text-base font-normal leading-snug text-zinc-300">
              Here is your{' '}
              <span className="font-light text-zinc-400">daily briefing.</span>
            </p>
            <button
              onClick={onClose}
              className="absolute right-4 top-4 z-10 rounded-xl p-2 text-zinc-400 transition-colors hover:bg-zinc-800/80 hover:text-zinc-100"
            >
              <FiX className="h-4 w-4" />
            </button>
          </div>

          <div className="bg-1 relative z-10 min-h-0 flex-1 overflow-hidden">
            <TodoPanel compact />
          </div>
        </div>
      )}

      {/* RIGHT CARD: emails / meetings */}
      <div
        className={`relative z-10 flex h-full min-w-0 flex-1 flex-col overflow-hidden rounded-xl border border-zinc-700 md:rounded-2xl ${
          weatherVideoSrc ? 'bg-zinc-900/80 backdrop-blur-xl' : 'bg-1'
        }`}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-zinc-700 px-4 pb-3 pt-4">
          <div>
            <p className="text-[10px] text-zinc-400">Catch Up</p>
            <h2 className="text-base font-semibold leading-tight text-zinc-100">
              {briefingTabs[activeTab].label}
            </h2>
          </div>
          <div className="flex items-center gap-1 rounded-xl border border-zinc-700/60 bg-zinc-800/80 p-1">
            {Object.entries(briefingTabs).map(([key, t]) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-all
                  ${activeTab === key ? 'bg-[#5652CF] text-white shadow-sm' : 'text-zinc-400 hover:text-zinc-200'}`}
              >
                {t.icon}
                <span>{t.label}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="bg-1 flex min-h-0 flex-1 flex-col overflow-hidden">
          {activeTab === 'emails' && (
            <EmailPanel
              onStageChat={(...args) => {
                onClose();
                onStageChat?.(...args);
              }}
              expandedEmailId={briefingExpandedEmailId}
              setExpandedEmailId={setBriefingExpandedEmailId}
              onExpandEmail={setExpandedBriefingEmail}
            />
          )}
          {ENABLE_MEETINGS && activeTab === 'meetings' && (
            <MeetingsPanel onStageChat={onStageChat} />
          )}
        </div>
        <div className="bg-1 shrink-0 border-t border-zinc-700 px-4 py-3">
          <button
            onClick={onClose}
            className="group flex w-full items-center justify-center gap-2 text-xs font-medium text-zinc-400 transition-colors hover:text-zinc-200"
          >
            <span>Finish Morning Briefing</span>
            <FiArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

// Animated pill that slides beneath the active tab button, measured off the
// DOM so it works across the workspace/personal tab groups without knowing
// their layout in advance.
const ActiveTabIndicator = ({ activeTab }) => {
  const containerRef = useRef(null);
  const [pos, setPos] = useState({
    width: 0,
    x: 0,
    opacity: 0,
    animate: false,
  });
  const isFirstRender = useRef(true);

  useEffect(() => {
    const container = containerRef.current?.parentElement;
    if (!container) return;

    const activeBtn = container.querySelector(`[data-tab="${activeTab}"]`);
    if (!activeBtn) return;

    const containerRect = container.getBoundingClientRect();
    const btnRect = activeBtn.getBoundingClientRect();

    const x = btnRect.left - containerRect.left;
    const w = btnRect.width;

    if (isFirstRender.current) {
      isFirstRender.current = false;
      setPos({ width: w, x, opacity: 1, animate: false });
    } else {
      setPos({ width: w, x, opacity: 1, animate: true });
    }
  }, [activeTab]);

  return (
    <div
      ref={containerRef}
      aria-hidden
      className="pointer-events-none absolute left-0 top-0 z-0 h-full"
      style={{
        width: pos.width,
        transform: `translateX(${pos.x}px)`,
        opacity: pos.opacity,
        transition: pos.animate
          ? 'transform 250ms cubic-bezier(0.16, 1, 0.3, 1), width 250ms cubic-bezier(0.16, 1, 0.3, 1)'
          : 'none',
      }}
    >
      <div className="h-full w-full rounded-lg bg-zinc-700" />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main export
// ─────────────────────────────────────────────────────────────────────────────

const RightSideActionTracker = ({
  isMorningBriefing = false,
  onMorningBriefing,
  onStageChat,
}) => {
  // ── Workspace context ──
  const {
    currentToolCallStatus,
    conversations,
    setScrollToToolId,
    creatingTask,
    isReasoning,
    userGoogleId: accessToken,
  } = DataState();
  const { plan, planStatus } = useTaskPlan();
  const {
    notifications,
    openNotificationInContext,
    setActiveNotificationId,
    removeCollapsedNotificationId,
    reloadNotifications,
  } = useNotifications();
  const { openCRModal } = useChangeRequests();

  // ── Meeting detail modal ──
  const [detailMeeting, setDetailMeeting] = useState(null);

  // ── Questions from the pipelines (on top of Waiting) ──
  const { questions, reload: reloadQuestions } = useAgentQuestions(
    ENABLE_AGENT_QUESTIONS ? accessToken : null,
  );
  const [openQuestionId, setOpenQuestionId] = useState(null);
  const [refreshingWaiting, setRefreshingWaiting] = useState(false);
  // Questions and the meetings waiting for review, fetched again.
  const refreshWaiting = async () => {
    setRefreshingWaiting(true);
    try {
      await Promise.all([reloadQuestions(), reloadNotifications?.()]);
    } finally {
      setRefreshingWaiting(false);
    }
  };

  // ── Tickets (mail/chat -> scope classifier -> Jira) ──
  const [tickets, setTickets] = useState([]);
  const [activeTicketId, setActiveTicketId] = useState(null);
  const reloadTickets = useCallback(async () => {
    if (!accessToken) return;
    try {
      const res = await listTickets(accessToken);
      setTickets(res?.tickets || []);
    } catch (e) {
      // Non-fatal -- the tab just shows nothing rather than an error state.
    }
  }, [accessToken]);
  useEffect(() => {
    reloadTickets();
  }, [reloadTickets]);
  const activeTicket =
    tickets.find((t) => t.ticket_id === activeTicketId) || null;

  // ── Local state ──
  const [showAllGoals, setShowAllGoals] = useState(false);
  const [activeTab, setActiveTab] = useState('tasks'); // tasks | notifications | waitingEvents | todo | meetings | emails
  const [briefingExpandedEmailId, setBriefingExpandedEmailId] = useState(null);
  // A pipeline may have asked something since: fresh each time Waiting opens.
  useEffect(() => {
    if (activeTab === 'waitingEvents') reloadQuestions();
  }, [activeTab, reloadQuestions]);
  const [mainExpandedEmailId, setMainExpandedEmailId] = useState(null);

  const handleViewEmailInBriefing = (email) => {
    setBriefingExpandedEmailId(email.message_id || email.id);
    onMorningBriefing?.(true);
  };

  const [isLastChatToolCalls, setIsLastChatToolCalls] = useState(false);
  useEffect(() => {
    conversations.forEach((c) => {
      if (c?.role === 'assistant') {
        setIsLastChatToolCalls(
          c?.tool_calls &&
            Array.isArray(c?.tool_calls) &&
            c?.tool_calls.length > 0,
        );
      }
    });
  }, [conversations, currentToolCallStatus]);

  const isCurrent = isLastChatToolCalls;
  const successfulTasks = currentToolCallStatus.filter(
    (a) => a.status === 'success',
  ).length;

  // Derive pending waiting items: processed meetings that need Jira review/approval
  const derivedWaitingEvents = notifications.filter(
    (n) =>
      n.source_type === 'meeting' &&
      ['processed', 'reprocessing'].includes(n.transcript_status),
  );

  // Count pending Jira updates across all waiting meetings
  const pendingJiraCount = derivedWaitingEvents.reduce((sum, n) => {
    const tasks = n.llm_summary?.task_wise_summary || [];
    const updates = tasks.reduce(
      (s, t) => s + (t.proposed_jira_issue_updates?.length || 0),
      0,
    );
    const actions = (n.llm_summary?.extracted_action_items || []).length;
    return sum + updates + actions;
  }, 0);

  const handleNotificationClick = (notification) => {
    if (notification.source_type === 'change_request') {
      openCRModal(notification.cr_data);
      return;
    }
    openNotificationInContext(notification.id);
    setActiveNotificationId(notification.id);
    removeCollapsedNotificationId(notification.id);
  };

  // ── Tab definitions ──
  // Workspace group
  const workspaceTabs = {
    tasks: { label: 'Tasks', icon: <ListTodo className="h-3.5 w-3.5" /> },
    notifications: {
      label: 'Notifications',
      icon: <Bell className="h-3.5 w-3.5" />,
    },
    ...(ENABLE_MEETINGS || ENABLE_AGENT_QUESTIONS
      ? {
          waitingEvents: {
            label: 'Waiting',
            icon: <FiClock className="h-3.5 w-3.5" />,
            count:
              questions.length +
              (pendingJiraCount > 0
                ? pendingJiraCount
                : derivedWaitingEvents.length),
          },
        }
      : {}),
    tickets: {
      label: 'Tickets',
      icon: <TicketIcon className="h-3.5 w-3.5" />,
      count: tickets.filter((t) => t.status === 'CLARIFICATION_REQUIRED')
        .length,
    },
  };
  // Personal group
  const personalTabs = {
    todo: { label: 'To Do', icon: <FiCheck className="h-3.5 w-3.5" /> },
    ...(ENABLE_MEETINGS
      ? {
          meetings: {
            label: 'Meetings',
            icon: <FiVideo className="h-3.5 w-3.5" />,
          },
        }
      : {}),
    ...(ENABLE_RIGHT_SIDEBAR_COMMUNICATION
      ? {
          emails: { label: 'Emails', icon: <FiMail className="h-3.5 w-3.5" /> },
        }
      : {}),
    ...(ENABLE_TEAMS_DM
      ? {
          chats: {
            label: 'Chats',
            icon: <FiMessageSquare className="h-3.5 w-3.5" />,
          },
        }
      : {}),
  };

  const allTabs = { ...workspaceTabs, ...personalTabs };
  const isWorkspaceTab = (key) => key in workspaceTabs;

  // ── Workspace content renderer ──
  const renderWorkspaceContent = () => {
    switch (activeTab) {
      case 'notifications':
        return (
          <div className="sc flex-1 divide-y divide-zinc-800/50 overflow-y-auto">
            {notifications.length === 0 ? (
              <p className="p-6 text-center text-xs italic text-zinc-500">
                No notifications yet.
              </p>
            ) : (
              notifications.map((n) => (
                <NotificationItem
                  key={n.id}
                  notification={n}
                  onClick={() => handleNotificationClick(n)}
                />
              ))
            )}
          </div>
        );
      case 'waitingEvents':
        if (openQuestionId) {
          return (
            <QuestionThread
              accessToken={accessToken}
              questionId={openQuestionId}
              onBack={() => setOpenQuestionId(null)}
              onDone={reloadQuestions}
            />
          );
        }
        return (
          <div className="sc flex-1 divide-y divide-zinc-800/50 overflow-y-auto">
            <div className="flex items-center justify-end px-3 py-1">
              <button
                type="button"
                onClick={refreshWaiting}
                disabled={refreshingWaiting}
                aria-label="Refresh waiting"
                title="Refresh"
                className="rounded-lg p-1.5 text-zinc-500 transition-colors hover:bg-zinc-800 hover:text-zinc-300 disabled:opacity-50"
              >
                <FiRefreshCw
                  className={`h-3.5 w-3.5 ${refreshingWaiting ? 'animate-spin' : ''}`}
                />
              </button>
            </div>
            {/* Questions for the member come first, as chats. */}
            {questions.map((q) => (
              <QuestionItem
                key={q.id}
                question={q}
                onClick={() => setOpenQuestionId(q.id)}
              />
            ))}
            {derivedWaitingEvents.length === 0
              ? questions.length === 0 && (
                  <p className="p-6 text-center text-xs italic text-zinc-500">
                    Nothing waiting.
                  </p>
                )
              : derivedWaitingEvents.map((e) => (
                  <WaitingEventItem
                    key={e.id}
                    event={e}
                    onClick={() =>
                      setDetailMeeting({
                        eventId: e.id,
                        isManual: !!e.user_provided_meeting,
                      })
                    }
                  />
                ))}
          </div>
        );
      case 'tickets':
        return (
          <div className="sc flex-1 divide-y divide-zinc-800/50 overflow-y-auto">
            {tickets.length === 0 ? (
              <p className="p-6 text-center text-xs italic text-zinc-500">
                No tickets yet.
              </p>
            ) : (
              tickets.map((t) => (
                <TicketItem
                  key={t.ticket_id}
                  ticket={t}
                  onClick={() => setActiveTicketId(t.ticket_id)}
                />
              ))
            )}
          </div>
        );
      case 'tasks':
      default:
        if (creatingTask) return <WaitingForTask />;
        return currentToolCallStatus.length > 0 ? (
          <div className="sc flex-1 divide-y divide-zinc-800/50 overflow-y-auto">
            {currentToolCallStatus.map((tool, i) => (
              <ActionItem
                key={i}
                action={tool}
                onActionClick={setScrollToToolId}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-1 items-center justify-center p-6 text-center text-xs italic text-zinc-500">
            No actions initiated yet.
          </div>
        );
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Morning Briefing mode
  // ─────────────────────────────────────────────────────────────────────────
  if (isMorningBriefing) {
    return (
      <>
        <div className="flex h-full w-full flex-col overflow-hidden">
          <MorningBriefingView
            onClose={() => {
              onMorningBriefing?.(false);
              setBriefingExpandedEmailId(null);
            }}
            onStageChat={onStageChat}
            briefingExpandedEmailId={briefingExpandedEmailId}
            setBriefingExpandedEmailId={setBriefingExpandedEmailId}
          />
        </div>
        <Styles />
      </>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Normal sidebar mode
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="bg-1 flex h-full w-full flex-col overflow-hidden rounded-xl border border-zinc-700">
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className="flex shrink-0 items-center justify-between border-b border-zinc-700 px-4 py-3">
          <div className="min-w-0">
            <h1 className="truncate text-lg font-semibold leading-tight tracking-tight text-zinc-100">
              {allTabs[activeTab]?.label ?? 'Workspace'}
            </h1>
          </div>

          {/* Running badge (tasks only) */}
          {activeTab === 'tasks' &&
            !creatingTask &&
            (isCurrent || planStatus !== 'idle') && (
              <div className="ml-2 flex shrink-0 items-center gap-2 rounded-full border border-blue-700/60 bg-blue-900/50 px-2.5 py-1">
                <div className="h-2 w-2 animate-pulse rounded-full bg-blue-400" />
                <span className="text-xs font-medium text-blue-300">
                  Running
                </span>
              </div>
            )}
        </div>

        {/* ── Tab bar: two groups separated by a divider ──────────────────── */}
        <div className="relative flex shrink-0 items-center gap-0.5 border-b border-zinc-800 px-3 py-2">
          <ActiveTabIndicator activeTab={activeTab} />

          {/* Workspace group */}
          {Object.entries(workspaceTabs).map(([key, t]) => (
            <button
              key={key}
              data-tab={key}
              onClick={() => setActiveTab(key)}
              title={t.label}
              className={`relative z-10 flex min-w-0 shrink items-center justify-center gap-1.5 rounded-lg px-2 py-1.5 text-[11px] font-medium transition-colors
                ${
                  activeTab === key
                    ? 'text-zinc-100'
                    : 'text-zinc-500 hover:bg-zinc-800/40 hover:text-zinc-300'
                }`}
            >
              {t.icon}
              {t.count > 0 && (
                <span className="absolute -right-0.5 -top-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-amber-500 text-[8px] font-bold text-black">
                  {t.count > 9 ? '9+' : t.count}
                </span>
              )}
            </button>
          ))}

          {/* Divider */}
          <div className="relative z-10 mx-1.5 h-4 w-px shrink-0 bg-zinc-700" />

          {/* Personal group */}
          {Object.entries(personalTabs).map(([key, t]) => (
            <button
              key={key}
              data-tab={key}
              onClick={() => setActiveTab(key)}
              title={t.label}
              className={`relative z-10 flex min-w-0 shrink items-center justify-center gap-1.5 rounded-lg px-2 py-1.5 text-[11px] font-medium transition-colors
                ${
                  activeTab === key
                    ? 'text-zinc-100'
                    : 'text-zinc-500 hover:bg-zinc-800/40 hover:text-zinc-300'
                }`}
            >
              {t.icon}
              {/* <span className="hidden lg:inline">{t.label}</span> */}
            </button>
          ))}
        </div>

        {/* ── Task Plan (workspace: tasks tab only) ───────────────────────── */}
        {activeTab === 'tasks' &&
          !creatingTask &&
          isReasoning &&
          plan &&
          plan.length > 0 && (
            <div className="shrink-0">
              <div className="flex items-center justify-between border-b border-zinc-700 px-4 py-2">
                <div className="flex items-center py-1">
                  {planStatus !== 'idle' && (
                    <div className="mr-4 flex items-center space-x-1.5">
                      {[0, 0.3, 0.6].map((delay, i) => (
                        <div
                          key={i}
                          className="h-2 w-2 animate-pulse rounded-full bg-blue-400"
                          style={{ animationDelay: `${delay}s` }}
                        />
                      ))}
                    </div>
                  )}
                  <h3 className="text-xs font-medium uppercase tracking-wider text-zinc-400">
                    {planStatus === 'planning'
                      ? 'Planning'
                      : planStatus === 'analyzing'
                        ? 'Analyzing'
                        : 'Plan'}
                  </h3>
                </div>
                {plan.length > RECENT_GOAL_COUNT && (
                  <button
                    onClick={() => setShowAllGoals((p) => !p)}
                    title={showAllGoals ? 'Show recent' : 'Show all'}
                    className="rounded-md p-1 text-zinc-400 transition-colors hover:bg-zinc-700 hover:text-zinc-100"
                  >
                    {showAllGoals ? (
                      <ChevronUp className="h-3 w-4" />
                    ) : (
                      <Ellipsis className="h-3 w-4" />
                    )}
                  </button>
                )}
              </div>
              <TaskPlan showAll={showAllGoals} />
            </div>
          )}

        {/* ── Content area ────────────────────────────────────────────────── */}
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          {/* Workspace tabs */}
          {isWorkspaceTab(activeTab) && renderWorkspaceContent()}

          {/* Personal tabs */}
          {activeTab === 'todo' && (
            <TodoPanel
              onCrApproved={() => setActiveTab('notifications')}
              onStageChat={onStageChat}
            />
          )}
          {ENABLE_MEETINGS && activeTab === 'meetings' && (
            <MeetingsPanel onStageChat={onStageChat} />
          )}
          {ENABLE_RIGHT_SIDEBAR_COMMUNICATION && activeTab === 'emails' && (
            <EmailPanel
              onStageChat={onStageChat}
              onViewEmailInBriefing={handleViewEmailInBriefing}
              expandedEmailId={mainExpandedEmailId}
              setExpandedEmailId={setMainExpandedEmailId}
            />
          )}
          {ENABLE_TEAMS_DM && activeTab === 'chats' && (
            <TeamsDmPanel accessToken={accessToken} />
          )}
        </div>

        {/* ── Footer bar (tasks count + morning briefing) ──────────────────── */}
        <div className="shrink-0 border-t border-zinc-800">
          {activeTab === 'tasks' &&
            !creatingTask &&
            currentToolCallStatus.length > 0 && (
              <div className="flex justify-between border-b border-zinc-800 bg-zinc-900/40 px-4 py-2 text-[11px] text-zinc-500">
                <span>{successfulTasks} completed</span>
                <span>{currentToolCallStatus.length} total</span>
              </div>
            )}
          {ENABLE_RIGHT_SIDEBAR_COMMUNICATION && (
            <div className="px-4 py-3">
              <button
                onClick={() => onMorningBriefing?.(true)}
                className="group flex w-full items-center justify-center gap-3 text-sm font-medium text-zinc-400 transition-colors hover:text-zinc-200"
              >
                <span>Morning Briefing</span>
                <FiArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </button>
            </div>
          )}
        </div>
      </div>

      {activeTicket && (
        <TicketDetailModal
          ticket={activeTicket}
          accessToken={accessToken}
          onStageChat={onStageChat}
          onClose={() => setActiveTicketId(null)}
          onUpdated={(updated) => {
            setTickets((prev) =>
              prev.map((t) =>
                t.ticket_id === updated.ticket_id ? updated : t,
              ),
            );
          }}
        />
      )}

      {detailMeeting && (
        <MeetingDetailModal
          eventId={detailMeeting.eventId}
          isManual={detailMeeting.isManual}
          onClose={() => setDetailMeeting(null)}
        />
      )}

      <Styles />
    </>
  );
};

function Styles() {
  return (
    <style>{`
      .sc::-webkit-scrollbar { width: 4px; }
      .sc::-webkit-scrollbar-track { background: transparent; }
      .sc::-webkit-scrollbar-thumb { background: #3f3f46; border-radius: 4px; }
      @keyframes fadeIn { from { opacity:0; transform:translateY(-4px); } to { opacity:1; transform:translateY(0); } }
      .animate-fi { animation: fadeIn .15s ease-out; }
      @keyframes slideIn { from { opacity:0; transform:translateX(10px); } to { opacity:1; transform:translateX(0); } }
      .animate-si { animation: slideIn .2s ease-out; }
    `}</style>
  );
}

export default RightSideActionTracker;
